import { getServicesBaseUrl, isQaEnv } from "../../helpers/apiEnv"
import React, { useState, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
	Row,
	Col,
	Card,
	CardBody,
	CardTitle,
	Form,
	FormGroup,
	Label,
	Input,
	Nav,
	NavItem,
	NavLink,
	TabContent,
	TabPane,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Spinner,
	UncontrolledTooltip,
	UncontrolledDropdown,
	ButtonDropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem,
} from "reactstrap";
import Select from "react-select";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DetalleFacturaDrawer from "./DetalleFactura/index.jsx";
import "./Facturas.css";
import "./ComprobantesStyles.css";
import * as XLSX from "xlsx-js-style";
import JSZip from "jszip";

const baseURL = process.env.REACT_APP_API_URL;
const baseURLPDF = process.env.REACT_APP_API_PDF;
const newAPIURL = getServicesBaseUrl().replace(/\/+$/, ""); // dinámico: respeta Prod/QA
//const newAPIURL = "http://localhost:3100/a2/api/v1";

// Endpoints del servicio de representación gráfica (responden PDF en base64).
// En QA el servicio de PDF apunta a pdf-test; en prod a pdf.
const PDF_BASE = isQaEnv() ? "https://pdf-test.ls-da.com" : "https://pdf.ls-da.com";
const PDF_GENERAR_ENDPOINT = `${PDF_BASE}/api/rep-grafica/generar-por-cufe-base64`;
const PDF_ESTAMPAR_ENDPOINT = `${PDF_BASE}/api/rep-grafica/estampar-por-cufe-base64`;
// Documentos equivalentes (05 y 95): siempre se generan con rep-grafica,
// sin importar el tipoPdf configurado en la empresa.
const TIPOS_DOC_REP_GRAFICA = ["05", "95"];

/**
 * Resuelve qué endpoint de PDF base64 usar para un comprobante.
 * Devuelve null cuando debe usarse el servicio timbrador (GET blob).
 *
 * Prioridad:
 *   1) Documento equivalente 05/95 → generar-por-cufe-base64 (regla fija).
 *   2) Empresa V1, V5 o V6         → generar-por-cufe-base64.
 *   3) Empresa V2                   → estampar-por-cufe-base64.
 *   4) Resto                        → null (timbrador).
 */
const resolvePdfBase64Endpoint = (tipoPdf, tipoComprobante) => {
	const tc = String(tipoComprobante ?? "").trim().padStart(2, "0");
	if (TIPOS_DOC_REP_GRAFICA.includes(tc)) return PDF_GENERAR_ENDPOINT;
	if (tipoPdf === "V1" || tipoPdf === "V5" || tipoPdf === "V6" || tipoPdf === "V7") return PDF_GENERAR_ENDPOINT;
	if (tipoPdf === "V2") return PDF_ESTAMPAR_ENDPOINT;
	return null;
};

// El servicio de ZIP (timbrador /a7/zip/generate) solo reconoce V1–V4.
// Los tipos nuevos V5/V6 usan el mismo render "generar" que V1 (el endpoint
// de PDF ignora la versión: sólo usa el CUFE), así que para el ZIP los
// mapeamos a V1 y así evitamos el error del back con "V6".
const resolveTipoPdfZip = (tipoPdf) => {
	if (tipoPdf === "V5" || tipoPdf === "V6" || tipoPdf === "V7") return "V1";
	return tipoPdf;
};

// Forma de pago DIAN: 1 = Contado, 2 = Crédito. Se toma de la factura
// (tolera código numérico o texto); "—" si el back no la envía.
const formaPagoLabel = (f) => {
	const raw = f?.metodo_pago ?? f?.forma_pago ?? f?.formapago ?? f?.forma_de_pago ?? f?.formaPago ?? f?.tipo_pago
	if (raw === null || raw === undefined || raw === "") return "—"
	const v = String(raw).trim().toLowerCase()
	if (v === "1" || v === "contado") return "Contado"
	if (v === "2" || v === "credito" || v === "crédito") return "Crédito"
	return String(raw)
}

// Catálogo DIAN — tipo de organización del receptor (persona jurídica / natural).
const TIPO_RECEPTOR_DIAN = {
	"1": "Persona Jurídica",
	"2": "Persona Natural",
};
const getTipoReceptorNombre = (code) => {
	const c = String(code ?? "").trim();
	if (!c) return "N/A";
	return TIPO_RECEPTOR_DIAN[c] || c;
};

// Catálogo DIAN — tipo de documento de identificación del receptor.
const TIPO_DOC_RECEPTOR_DIAN = {
	"11": "Registro civil",
	"12": "Tarjeta de identidad",
	"13": "Cédula de ciudadanía",
	"21": "Tarjeta de extranjería",
	"22": "Cédula de extranjería",
	"31": "NIT",
	"41": "Pasaporte",
	"42": "Doc. identificación extranjero",
	"43": "Sin identificación del exterior",
	"44": "RUT extranjero",
	"46": "Tipo de documento diferente",
	"47": "PEP",
};
const getTipoDocReceptorNombre = (code) => {
	const c = String(code ?? "").trim();
	if (!c) return "N/A";
	return TIPO_DOC_RECEPTOR_DIAN[c] || c;
};

// Helper: fecha local como YYYY-MM-DD (evita desfase UTC)
const toLocalDateString = (date) => {
	if (!date) return null;
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
};



// Función para formatear números como moneda
const formatCurrency = (value) => {
	return new Intl.NumberFormat("es-CO", {
		style: "currency",
		currency: "COP",
	}).format(value);
};

// Función para formatear fechas evitando problemas de zona horaria
const formatFecha = (fechaString) => {
	if (!fechaString) return "N/A";

	let fecha;

	// Caso específico: Si la fecha viene como "YYYY-MM-DDTHH:mm:ssZ" con medianoche UTC
	// Esto indica que es solo una fecha, no una fecha/hora específica
	if (typeof fechaString === 'string' && fechaString.match(/^\d{4}-\d{2}-\d{2}T00:00:00Z$/)) {
		// Extraer solo la parte de la fecha y crear una fecha local
		const fechaSoloFecha = fechaString.split('T')[0]; // "2025-08-24"
		const [year, month, day] = fechaSoloFecha.split('-').map(Number);
		fecha = new Date(year, month - 1, day); // Crear fecha local sin conversión UTC
	}
	// Caso general: Si la fecha viene como string ISO con tiempo
	else if (typeof fechaString === 'string' && fechaString.includes('T')) {
		// Si tiene zona horaria explícita, usarla directamente
		if (fechaString.includes('Z') || fechaString.includes('+') || fechaString.includes('-', 19)) {
			fecha = new Date(fechaString);
		} else {
			// Si no tiene zona horaria, agregarle la zona local colombiana
			fecha = new Date(fechaString + '-05:00');
		}
	}
	// Si la fecha viene como string de fecha (YYYY-MM-DD)
	else if (typeof fechaString === 'string' && fechaString.match(/^\d{4}-\d{2}-\d{2}$/)) {
		// Crear fecha directamente sin conversión UTC usando los componentes
		const [year, month, day] = fechaString.split('-').map(Number);
		fecha = new Date(year, month - 1, day); // month es 0-indexed en JS
	}
	// Si viene en otro formato, usar constructor normal
	else {
		fecha = new Date(fechaString);
	}

	// Verificar si la fecha es válida
	if (isNaN(fecha.getTime())) return "N/A";

	const resultado = fecha.toLocaleDateString("es-CO", {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit'
	});

	return resultado;
};

// Función para formatear números como porcentaje
const Facturas = (props) => {
	document.title = "Comprobantes | LS-DA - Softlogy";

	const navigate = useNavigate();

	const [facturas, setFacturas] = useState([]);
	const [totalPages, setTotalPages] = useState(0);
	const [totalDocs, setTotalDocs] = useState(0);
	const [loading, setLoading] = useState(false);
	const [documentos, setDocumentos] = useState([]);
	const [empresas, setEmpresas] = useState([]);

	const [nitemisor, setNitemisor] = useState("");
	const [fechaRango, setFechaRango] = useState([new Date(), new Date()]);
	const [fechaInicio, fechaFin] = fechaRango;
	const [tipoDocumento, setTipoDocumento] = useState("TODOS LOS DOCUMENTOS");
	const [campoFecha, setCampoFecha] = useState("fecha"); // "fecha" | "fechageneracion"
	const [cufe, setCufe] = useState("");
	const [nitReceptor, setNitReceptor] = useState(""); // Nuevo parámetro
	const [folio, setFolio] = useState(""); // Nuevo parámetro - cambio de factura a folio
	const [currentPage, setCurrentPage] = useState(0);

	const [modal, setModal] = useState(false);
	const [modalContent] = useState("");
	const [detalleModal, setDetalleModal] = useState(false);
	const [detalleContent, setDetalleContent] = useState(null);
	// Nuevo drawer de detalle (reemplaza el modal viejo de "Conceptos")
	const [detalleDrawerAbierto, setDetalleDrawerAbierto] = useState(false);
	const [detalleCufe, setDetalleCufe] = useState(null);
	const [sucursales, setSucursales] = useState([]);
	const [selectedSucursal, setSelectedSucursal] = useState("");

	const [selectedPrefijo, setSelectedPrefijo] = useState("");
	const [tipoTercero, setTipoTercero] = useState("todos");
	// Filtros nuevos: delivery y datáfono (con | sin | todas). "todas" = sin filtro.
	const [filtroDelivery, setFiltroDelivery] = useState("todas");
	const [filtroDatafono, setFiltroDatafono] = useState("todas");

	// Estados adicionales para el rango de fechas (ya no necesitamos modal)
	// Las fechas se manejan directamente con fechaRango

	// Estados para totales de paginación
	const [totalSubtotal, setTotalSubtotal] = useState(0);
	const [totalImpuestos, setTotalImpuestos] = useState(0);
	const [totalGeneral, setTotalGeneral] = useState(0);
	const [totalIva, setTotalIva] = useState(0);
	const [totalInc, setTotalInc] = useState(0);
	const [totalOtrosImpuestos, setTotalOtrosImpuestos] = useState(0);
	const [totalCargoPropina, setTotalCargoPropina] = useState(0);

	const toggleModal = () => setModal(!modal);
	const toggleDetalleModal = () => setDetalleModal(!detalleModal);

	const [loadingPDFs, setLoadingPDFs] = useState({});
	const [loadingZips, setLoadingZips] = useState({});
	const [downloadProgresses, setDownloadProgresses] = useState({});

	// ─── Multi-select & Bulk Download ───
	const [selectedCufes, setSelectedCufes] = useState({});
	const [bulkDownloading, setBulkDownloading] = useState(false);
	const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, errors: 0 });
	const [bulkType, setBulkType] = useState(null); // "pdf" | "zip" — qué descarga masiva está corriendo

	const selectedCount = Object.keys(selectedCufes).length;

	const toggleSelect = (cufe) => {
		setSelectedCufes(prev => {
			const next = { ...prev };
			if (next[cufe]) { delete next[cufe]; } else { next[cufe] = true; }
			return next;
		});
	};

	const toggleSelectAll = () => {
		if (selectedCount === facturas.filter(f => f.cufe).length && facturas.length > 0) {
			setSelectedCufes({});
		} else {
			const obj = {};
			facturas.forEach(f => { if (f.cufe) obj[f.cufe] = true; });
			setSelectedCufes(obj);
		}
	};

	const clearSelection = () => setSelectedCufes({});

	const handleBulkPDFDownload = async () => {
		const cufes = Object.keys(selectedCufes);
		if (cufes.length === 0) return;

		setBulkType("pdf");
		setBulkDownloading(true);
		setBulkProgress({ current: 0, total: cufes.length, errors: 0 });
		let errorCount = 0;

		const zip = new JSZip();

		const storedEmpresaBulk = JSON.parse(localStorage.getItem("empresaSeleccionada") || "{}");
		const tipoPdfBulk = storedEmpresaBulk.tipoPdf; // "V1" | "V2" | null
		const storedUser = localStorage.getItem("authUser");
		const currentUser = storedUser ? JSON.parse(storedUser) : null;

		for (let i = 0; i < cufes.length; i++) {
			const cufe = cufes[i];
			const factura = facturas.find(f => f.cufe === cufe);
			if (!factura) { errorCount++; setBulkProgress({ current: i + 1, total: cufes.length, errors: errorCount }); continue; }

			try {
				let blob, filename;

				// Los documentos equivalentes (05/95) siempre usan generar-por-cufe-base64,
				// aunque la empresa no sea V1/V2.
				const pdfEndpoint = resolvePdfBase64Endpoint(tipoPdfBulk, factura.tipocomprobante);

				if (pdfEndpoint) {
					const response = await axios.post(
						pdfEndpoint,
						{ cufe },
						{ headers: { "Content-Type": "application/json" } }
					);
					const { filename: fn, pdfBase64 } = response.data;
					const byteChars = atob(pdfBase64);
					const byteNums = new Array(byteChars.length);
					for (let j = 0; j < byteChars.length; j++) byteNums[j] = byteChars.charCodeAt(j);
					blob = new Blob([new Uint8Array(byteNums)], { type: "application/pdf" });
					filename = fn || `comprobante_${cufe}.pdf`;
				} else {
					const response = await axios.get(`${baseURLPDF}invoice/pdf/${cufe}`, {
						responseType: "blob",
						headers: { Authorization: `Bearer ${currentUser?.access}` },
					});
					blob = new Blob([response.data], { type: "application/pdf" });
					filename = `${factura.prefijo || ""}${factura.folio || cufe}.pdf`;
				}

				zip.file(filename, blob);
			} catch (e) {
				console.error(`Error descargando PDF ${cufe}:`, e);
				errorCount++;
			}

			setBulkProgress({ current: i + 1, total: cufes.length, errors: errorCount });
		}

		// Generate and download ZIP
		if (Object.keys(zip.files).length > 0) {
			const zipBlob = await zip.generateAsync({ type: "blob" });
			const fecha = toLocalDateString(new Date());
			const url = window.URL.createObjectURL(zipBlob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `comprobantes_${fecha}.zip`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		}

		setBulkDownloading(false);
		setBulkType(null);
		setSelectedCufes({});
		const exitosos = cufes.length - errorCount;
		alert(`Descarga completada: ${exitosos} PDF${exitosos !== 1 ? 's' : ''} en ZIP${errorCount > 0 ? `, ${errorCount} con error` : ''}`);
	};

	// Descarga masiva de ZIPs firmados (uno por factura) y los empaqueta
	// en un único ZIP container. Cada ZIP individual mantiene su firma intacta.
	const handleBulkZipDownload = async () => {
		const cufes = Object.keys(selectedCufes);
		if (cufes.length === 0) return;

		const storedEmpresaZip = JSON.parse(localStorage.getItem("empresaSeleccionada") || "{}");
		const tipopdf = storedEmpresaZip.tipoPdf;
		const passwordCertificado = storedEmpresaZip.certificadoClave;

		if (!tipopdf) {
			alert("La empresa no tiene configurado el campo 'tipoPdf'. Configúralo antes de descargar.");
			return;
		}
		if (!passwordCertificado) {
			alert("La empresa no tiene configurado el campo 'certificadoClave'. Configúralo antes de descargar.");
			return;
		}

		setBulkType("zip");
		setBulkDownloading(true);
		setBulkProgress({ current: 0, total: cufes.length, errors: 0 });
		let errorCount = 0;

		const zipMaster = new JSZip();

		for (let i = 0; i < cufes.length; i++) {
			const cufe = cufes[i];
			const factura = facturas.find(f => f.cufe === cufe);

			try {
				const response = await axios.post(
					"https://timbrador.ls-da.com/a7/zip/generate?format=json",
					{ cufe, passwordCertificado, tipopdf: resolveTipoPdfZip(tipopdf) },
					{
						headers: {
							"Content-Type": "application/json",
							"x-api-key": "394beb1f7fcf7793dc79ba0a5fe2077cca2bf99c8407dfec5325a7335505f2a5",
						},
						timeout: 600000,
					}
				);
				const payload = response.data;
				const base64 =
					(typeof payload?.data === "string" ? payload.data : null) ||
					payload?.zipBase64 ||
					payload?.base64 ||
					payload?.archivo_base64;

				if (!base64) throw new Error("Respuesta sin base64");

				const filename =
					payload?.filename ||
					`${factura?.prefijo || ""}${factura?.folio || cufe}.zip`;

				// Decodificar base64 a binario y agregar al ZIP master
				const binary = atob(base64);
				const bytes = new Uint8Array(binary.length);
				for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j);
				zipMaster.file(filename, bytes);
			} catch (e) {
				console.error(`Error descargando ZIP ${cufe}:`, e);
				errorCount++;
			}

			setBulkProgress({ current: i + 1, total: cufes.length, errors: errorCount });
		}

		// Generar y descargar el ZIP master con los ZIPs adentro
		if (Object.keys(zipMaster.files).length > 0) {
			const zipBlob = await zipMaster.generateAsync({ type: "blob" });
			const fecha = toLocalDateString(new Date());
			const url = window.URL.createObjectURL(zipBlob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `comprobantes_zips_${fecha}.zip`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		}

		setBulkDownloading(false);
		setBulkType(null);
		setSelectedCufes({});
		const exitosos = cufes.length - errorCount;
		alert(`Descarga completada: ${exitosos} ZIP${exitosos !== 1 ? 's' : ''} firmado${exitosos !== 1 ? 's' : ''} agrupados${errorCount > 0 ? `, ${errorCount} con error` : ''}`);
	};

	const [correoModalOpen, setCorreoModalOpen] = useState(false);
	const [correoActiveTab, setCorreoActiveTab] = useState("trace");
	const [correoTraceLoading, setCorreoTraceLoading] = useState(false);
	const [correoTraceError, setCorreoTraceError] = useState("");
	// Bandera específica para el caso "no hay trazabilidad" (HTTP 404 del back):
	// no es un error técnico real, sino que ese CUFE aún no tiene registros en
	// correos_inbox — se renderiza con un estado vacío informativo, no rojo.
	const [correoTraceNotFound, setCorreoTraceNotFound] = useState(false);
	const [correoTraceData, setCorreoTraceData] = useState([]);
	const [correoTraceMeta, setCorreoTraceMeta] = useState(null);
	// Respuesta cruda enriquecida del nuevo endpoint /a7/inbox/{cufe}/full
	const [correoInboxFull, setCorreoInboxFull] = useState(null);
	const [correoMail, setCorreoMail] = useState("");
	const [correoTarget, setCorreoTarget] = useState(null);
	const [correoResendLoading, setCorreoResendLoading] = useState(false);

	const resetCorreoModal = () => {
		setCorreoTraceData([]);
		setCorreoTraceMeta(null);
		setCorreoInboxFull(null);
		setCorreoTraceError("");
		setCorreoTraceNotFound(false);
		setCorreoMail("");
		setCorreoActiveTab("timeline");
		setCorreoResendLoading(false);
	};

	const closeCorreoModal = () => {
		setCorreoModalOpen(false);
		setCorreoTarget(null);
		resetCorreoModal();
	};

	const fetchCorreoTrace = async ({ cufe }) => {
		if (!cufe) {
			setCorreoTraceError("No hay CUFE para consultar la trazabilidad de correo.");
			return;
		}

		setCorreoTraceLoading(true);
		setCorreoTraceError("");
		setCorreoTraceNotFound(false);
		setCorreoTraceData([]);
		setCorreoTraceMeta(null);
		setCorreoInboxFull(null);

		try {
			const response = await axios.get(
				`https://timbrador.ls-da.com/a7/inbox/${encodeURIComponent(cufe)}/full`,
				{
					headers: {
						"Content-Type": "application/json",
						"X-API-Key": "394beb1f7fcf7793dc79ba0a5fe2077cca2bf99c8407dfec5325a7335505f2a5",
					},
				}
			);

			if (response.data && response.data.success) {
				const full = response.data;
				setCorreoInboxFull(full);

				const docInfo = full.documento || {};
				const docLabel = `${docInfo.prefijo || ""}${docInfo.folio || ""}`.trim();
				const emisorLabel = docInfo.nombre_emisor || docInfo.nitemisor || "";

				// Mapeamos destinatarios → estructura `correoTraceData` (legacy) para no romper
				// el listado de eventos del modal. Cada intento de envío y cada evento SES
				// se traduce en un "evento" individual con record_type compatible.
				const mapeado = (full.destinatarios || []).map((d) => {
					const events = [];

					// Intentos de envío del back (listener) → Delivery o Failed
					(d.intentos_envio || []).forEach((att) => {
						const isOk = String(att.status || "").toLowerCase() === "sent";
						const durs = att.duration_ms || {};
						events.push({
							record_type: isOk ? "Delivery" : "Failed",
							event_date: (att.event_time || "").split(" ")[0] || "",
							event_time: att.event_time || "",
							recipient: d.email,
							cliente: docInfo.nombre_receptor || "",
							tag: att.error_stage || "",
							documento: docLabel,
							iddocumento: docInfo.tipo_documento || "",
							emisor: emisorLabel,
							platform: att.source || "",
							user_agent: "",
							geo_country: "",
							geo_city: "",
							original_link: "",
							details: att.error_message
								|| `${durs.total || 0}ms (download ${durs.download || 0}ms · pdf ${durs.pdf || 0}ms · email ${durs.email || 0}ms)`,
							message_stream: att.source || "",
							server_id: att.http_status_code != null ? `HTTP ${att.http_status_code}` : "",
							plataforma: att.source || "",
							message_id: d.message_id || "",
						});
					});

					// Eventos SES (Open, Click, Bounce, etc.)
					(d.eventos_ses || []).forEach((e) => {
						events.push({
							record_type: e.tipo || "",
							event_date: (e.event_time || "").split(" ")[0] || "",
							event_time: e.event_time || "",
							recipient: d.email,
							cliente: docInfo.nombre_receptor || "",
							tag: e.subtipo || "",
							documento: docLabel,
							iddocumento: docInfo.tipo_documento || "",
							emisor: emisorLabel,
							platform: "",
							user_agent: e.user_agent || "",
							geo_country: "",
							geo_city: "",
							original_link: "",
							details: e.ip_address ? `IP: ${e.ip_address}` : "",
							message_stream: "",
							server_id: "",
							plataforma: "SES",
							message_id: d.message_id || "",
						});
					});

					return { message_id: d.message_id || "", events };
				});

				// Ordenamos los eventos por fecha descendente dentro de cada destinatario
				mapeado.forEach((m) => {
					m.events.sort((a, b) => (b.event_time || "").localeCompare(a.event_time || ""));
				});

				setCorreoTraceData(mapeado);

				const messageIdsList = (full.destinatarios || [])
					.map((d) => d.message_id)
					.filter(Boolean)
					.join(", ");

				setCorreoTraceMeta({
					documento: docLabel,
					emisor: emisorLabel,
					totalEvents: full.resumen?.total_eventos_ses ?? 0,
					messageIds: messageIdsList,
					timestamp: full.estado_actual?.ultimo_intento_at || new Date().toLocaleString("es-CO"),
				});
			} else {
				setCorreoTraceError(
					response.data?.error?.message || "No fue posible obtener la trazabilidad de correo."
				);
			}
		} catch (error) {
			// HTTP 404 = el CUFE no está en correos_inbox del nuevo servicio.
			// No es un error técnico — significa que esta factura todavía no tiene
			// trazabilidad registrada (envío anterior al nuevo sistema o no se
			// envió por correo todavía). Lo marcamos con una bandera distinta
			// para que la UI muestre un mensaje informativo amable.
			const status = error.response?.status;
			const code = error.response?.data?.code;
			const apiError = error.response?.data?.error;
			const apiErrorStr = typeof apiError === "string" ? apiError : (apiError?.message || "");

			if (status === 404 || code === "NOT_FOUND" || /no encontrado/i.test(apiErrorStr)) {
				setCorreoTraceNotFound(true);
				setCorreoTraceError("");
			} else {
				setCorreoTraceError(
					apiErrorStr ||
					error.response?.data?.message ||
					error.message ||
					"Error al consultar la trazabilidad de correo."
				);
			}
		} finally {
			setCorreoTraceLoading(false);
		}
	};

	const openCorreoModal = (factura) => {
		const target = {
			cufe: factura.cufe,
			prefijo: factura.prefijo,
			folio: factura.folio,
			nitemisor: factura.nitemisor,
		};
		setCorreoTarget(target);
		setCorreoModalOpen(true);
		setCorreoActiveTab("timeline");
		setCorreoMail("");
		fetchCorreoTrace(target);
	};

	const handleResendEmail = async () => {
		if (!correoTarget?.cufe) {
			alert("No se encontró el CUFE del comprobante.");
			return;
		}

		if (!correoMail || !correoMail.trim()) {
			alert("Debe ingresar un correo válido.");
			return;
		}

		const cufe = correoTarget.cufe;
		const mail = correoMail.trim();

		// Helper: reenviar usando el endpoint LEGACY (fallback cuando /a7/reenviar
		// devuelve 404 — por ejemplo cuando el CUFE no está aún en el inbox del nuevo
		// servicio EnviadorCorreos).
		const reenviarLegacy = async () => {
			await axios.post(
				"https://timbrador.ls-da.com/v2/factura/resendMail",
				{ cufe, mail },
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: "eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJzb2Z0bG9neSIsImF1ZGllbmNlIjoid2ViIiwiY3JlYXRlZCI6MTczMjIxMTc5MTEyMCwiZXhwIjoyNzMyMjExNzkwfQ.q6_8G2cViYb3l7NU5R9gAWNo1JQz-afRkc-mPnuc9n2iIYUJlbq6XHzm-K_0ldnQmmkxFbRofiXkVAB41_Me5Q",
					},
				}
			);
		};

		try {
			setCorreoResendLoading(true);
			let usadoLegacy = false;

			try {
				// Intento 1 — endpoint nuevo /a7/reenviar
				await axios.post(
					"https://timbrador.ls-da.com/a7/reenviar",
					{ cufe, mailReceptor: mail },
					{
						headers: {
							"Content-Type": "application/json",
							"X-API-Key": "394beb1f7fcf7793dc79ba0a5fe2077cca2bf99c8407dfec5325a7335505f2a5",
						},
					}
				);
			} catch (err) {
				// Fallback solo si el back nuevo respondió 404 (CUFE no encontrado en su inbox).
				// Cualquier otro error se propaga al catch externo.
				if (err.response?.status === 404) {
					console.warn("Reenvío /a7/reenviar devolvió 404, intentando legacy /v2/factura/resendMail…");
					await reenviarLegacy();
					usadoLegacy = true;
				} else {
					throw err;
				}
			}

			alert(usadoLegacy
				? "Correo reenviado con éxito (servicio legacy)."
				: "Correo reenviado con éxito.");
			setCorreoMail("");
			// Refrescamos la trazabilidad para ver el nuevo intento en el inbox
			fetchCorreoTrace({ cufe });
			setCorreoActiveTab("timeline");
		} catch (error) {
			console.error("Error reenvío correo:", error);
			const msg = error.response?.data?.error?.message
				|| error.response?.data?.message
				|| error.message
				|| "Ocurrió un error al reenviar el correo electrónico.";
			alert(msg);
		} finally {
			setCorreoResendLoading(false);
		}
	};

	const handleDianClick = (cufe) => {
		// En QA el documento está en habilitación (catalogo-vpfe-hab); en prod en producción.
		const dianBase = isQaEnv() ? "https://catalogo-vpfe-hab.dian.gov.co" : "https://catalogo-vpfe.dian.gov.co";
		const url = `${dianBase}/User/SearchDocument?DocumentKey=${cufe}`;
		window.open(url, "_blank");
	};

	const handleDetalleClick = (cufe) => {
		// Abrir el drawer; el drawer hace su propio fetch a /facturas/{cufe}/detalles
		setDetalleCufe(cufe);
		setDetalleDrawerAbierto(true);
	};

	// Descarga el ZIP firmado (factura + adjuntos) desde el timbrador.
	// Toma tipoPdf y certificadoClave de la empresa seleccionada.
	const handleZipClick = async (cufe, factura) => {
		const storedEmpresaZip = JSON.parse(localStorage.getItem("empresaSeleccionada") || "{}");
		const tipopdf = storedEmpresaZip.tipoPdf;
		// El campo en la empresa se llama `certificadoClave`; el back lo espera como `passwordCertificado`
		const passwordCertificado = storedEmpresaZip.certificadoClave;

		if (!tipopdf) {
			alert("La empresa no tiene configurado el campo 'tipoPdf'. Configúralo antes de descargar el ZIP.");
			return;
		}
		if (!passwordCertificado) {
			alert("La empresa no tiene configurado el campo 'certificadoClave'. Configúralo antes de descargar el ZIP.");
			return;
		}

		setLoadingZips((prev) => ({ ...prev, [cufe]: true }));
		try {
			const response = await axios.post(
				"https://timbrador.ls-da.com/a7/zip/generate?format=json",
				{ cufe, passwordCertificado, tipopdf: resolveTipoPdfZip(tipopdf) },
				{
					headers: {
						"Content-Type": "application/json",
						"x-api-key": "394beb1f7fcf7793dc79ba0a5fe2077cca2bf99c8407dfec5325a7335505f2a5",
					},
					timeout: 600000, // 10 min — algunos ZIPs pueden ser pesados
				}
			);

			const payload = response.data;
			// Soporta varios shapes de respuesta del back: { data, filename }, { zipBase64 }, { base64 }, etc.
			const base64 =
				(typeof payload?.data === "string" ? payload.data : null) ||
				payload?.zipBase64 ||
				payload?.base64 ||
				payload?.archivo_base64;
			const contentType = payload?.content_type || "application/zip";
			const filename =
				payload?.filename ||
				`${factura?.prefijo || ""}${factura?.folio || cufe}.zip`;

			if (!base64) {
				throw new Error("La respuesta del servidor no contiene el archivo ZIP.");
			}

			// Decodificar base64 → Uint8Array → Blob
			const binary = atob(base64);
			const bytes = new Uint8Array(binary.length);
			for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
			const blob = new Blob([bytes], { type: contentType });

			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", filename);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch (err) {
			console.error("Error al descargar ZIP:", err);
			alert(
				`Error al descargar el ZIP: ${err.response?.data?.message || err.message || "verifica tu conexión."}`
			);
		} finally {
			setLoadingZips((prev) => {
				const next = { ...prev };
				delete next[cufe];
				return next;
			});
		}
	};

	const handlePDFClick = async (cufe, nitEmisorFactura, tipoComprobante) => {
		setLoadingPDFs((prevState) => ({ ...prevState, [cufe]: true }));
		setDownloadProgresses((prevState) => ({ ...prevState, [cufe]: 0 }));

		// Determinar endpoint según tipoPdf de la empresa y el tipo de documento.
		// Los documentos equivalentes (05/95) siempre usan generar-por-cufe-base64.
		const storedEmpresaPdf = JSON.parse(localStorage.getItem("empresaSeleccionada") || "{}");
		const tipoPdf = storedEmpresaPdf.tipoPdf; // "V1" | "V2" | null
		const pdfEndpoint = resolvePdfBase64Endpoint(tipoPdf, tipoComprobante);

		try {
			const storedUser = localStorage.getItem("authUser");
			const currentUser = storedUser ? JSON.parse(storedUser) : null;

			if (pdfEndpoint) {
				const response = await axios.post(
					pdfEndpoint,
					{ cufe: cufe },
					{
						headers: { "Content-Type": "application/json" },
						onDownloadProgress: (progressEvent) => {
							if (progressEvent.total) {
								const percentCompleted = Math.round(
									(progressEvent.loaded * 100) / progressEvent.total
								);
								setDownloadProgresses((prevState) => ({
									...prevState,
									[cufe]: percentCompleted,
								}));
							}
						},
					}
				);

				const { filename, pdfBase64 } = response.data;

				const byteCharacters = atob(pdfBase64);
				const byteNumbers = new Array(byteCharacters.length);
				for (let i = 0; i < byteCharacters.length; i++) {
					byteNumbers[i] = byteCharacters.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				const blob = new Blob([byteArray], { type: "application/pdf" });

				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", filename || `comprobante_${cufe}.pdf`);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			} else {
				// Servicio original timbrador
				const apiUrl = `${baseURLPDF}invoice/pdf/${cufe}`;

				const response = await axios.get(apiUrl, {
					responseType: "blob",
					headers: {
						Authorization: `Bearer ${currentUser.access}`,
					},
					onDownloadProgress: (progressEvent) => {
						const percentCompleted = Math.round(
							(progressEvent.loaded * 100) / progressEvent.total
						);
						setDownloadProgresses((prevState) => ({
							...prevState,
							[cufe]: percentCompleted,
						}));
					},
				});

				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", `comprobante_${cufe}.pdf`);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			}
		} catch (error) {
			console.error("Error al obtener el PDF:", error);
			alert(
				"Ocurrió un error al obtener el PDF. Por favor, inténtalo de nuevo."
			);
		} finally {
			setLoadingPDFs((prevState) => ({ ...prevState, [cufe]: false }));
			setDownloadProgresses((prevState) => ({ ...prevState, [cufe]: 0 }));
		}
	};

	const fetchFacturas = async (page = 1) => {
		setLoading(true);
		setSelectedCufes({}); // Clear selection on page change

		// Si hay CUFE, solo validar que esté presente
		if (cufe && cufe.trim() !== "") {
			// Solo consultar por CUFE
		} else if (nitemisor && selectedSucursal && selectedPrefijo && folio) {
			// Combinación especial: NIT Emisor + Sucursal + Prefijo + Folio (fechas opcionales)
		} else {
			// Verificar que todos los filtros obligatorios estén presentes
			if (!nitemisor || !fechaInicio || !fechaFin) {
				alert(
					"Debe completar todos los campos obligatorios: NIT emisor, fecha de inicio y fecha fin para realizar la consulta"
				);
				setLoading(false);
				return;
			}
		}

		try {
			// Construir los parámetros de la consulta
			const params = {
				page: page,
				page_size: 50,
				incluir_correos: true,
			};

			// Agregar filtros solo si tienen valores
			if (cufe && cufe.trim() !== "") {
				// Modo CUFE: solo enviar CUFE
				params.cufe = cufe;
			} else {
				// Modo normal: enviar todos los filtros aplicables
				if (nitemisor) params.nitemisor = nitemisor;
				if (fechaInicio)
					params.fecha_inicio = toLocalDateString(fechaInicio);
				if (fechaFin) params.fecha_fin = toLocalDateString(fechaFin);
				params.campo_fecha = campoFecha;
				if (tipoDocumento && tipoDocumento !== "TODOS LOS DOCUMENTOS")
					params.tipo_documento = tipoDocumento;
				if (selectedSucursal) params.sucursal = selectedSucursal;
				if (selectedPrefijo) params.prefijo = selectedPrefijo;
				if (nitReceptor) params.nitreceptor = nitReceptor;
				if (folio) params.folio = folio; // Folio como filtro adicional
				if (tipoTercero === "publico") params.publicogeneral = 0;
				if (tipoTercero === "personalizadas") params.publicogeneral = 1;
				// Filtros delivery / datáfono (solo si no es "todas")
				if (filtroDelivery && filtroDelivery !== "todas") params.delivery = filtroDelivery;
				if (filtroDatafono && filtroDatafono !== "todas") params.datafono = filtroDatafono;
			}

			// 🔍 LOG TEMPORAL: Mostrar parámetros enviados a la API - removed for clean console

			const response = await axios.get(`${newAPIURL}/facturas`, {
				params: params,
				headers: {
					"Content-Type": "application/json",
				},
			});

			if (response.data.success) {
				setFacturas(response.data.data || []);
				setTotalPages(response.data.pagination?.total_pages || 0);
				setTotalDocs(response.data.pagination?.total_records || 0);
				setCurrentPage(page - 1); // Ajustado a cero-indexado

				// Almacenar los totales de paginación
				const pagination = response.data.pagination || {};
				setTotalSubtotal(pagination.total_subtotal || 0);
				setTotalImpuestos(pagination.total_impuestos || 0);
				setTotalGeneral(pagination.total_general || 0);
				setTotalIva(pagination.total_iva || 0);
				setTotalInc(pagination.total_inc || 0);
				setTotalOtrosImpuestos(pagination.total_otros_impuestos || 0);
				setTotalCargoPropina(pagination.total_cargo_propina || 0);
			} else {
				alert(`Error: ${response.data.error?.message || "Error desconocido"}`);
				setFacturas([]);
				setTotalPages(0);
				setTotalDocs(0);
			}
		} catch (error) {
			alert("Error al consultar los datos. Verifique su conexión.");
			setFacturas([]);
			setTotalPages(0);
			setTotalDocs(0);
		} finally {
			setLoading(false);
		}
	};

	const fetchTipoDocumento = async () => {
		try {
			const response = await axios.get("/listas/TipoDocumento-2.1.xml");
			const xmlText = response.data;
			const parser = new DOMParser();
			const xmlDoc = parser.parseFromString(xmlText, "application/xml");
			const rows = xmlDoc.getElementsByTagName("Row");
			const types = [];

			for (let row of rows) {
				const codeValue = row.querySelector('[ColumnRef="code"] SimpleValue');
				const nameValue = row.querySelector('[ColumnRef="name"] SimpleValue');
				if (codeValue && nameValue) {
					types.push({
						code: codeValue.textContent,
						name: nameValue.textContent,
					});
				}
			}

			setDocumentos(types);
		} catch (error) {
		}
	};

	const fetchEmpresas = async () => {
		const storedUser = localStorage.getItem("authUser");
		const currentUser = storedUser ? JSON.parse(storedUser) : null;

		if (!currentUser) {
			return;
		}


		if (currentUser.role === "SuperAdmin") {
			// Obtener empresas desde la API
			if (!currentUser.IdEmpresa || !currentUser.access) {
				return;
			}
			try {
				const response = await axios.get(
					`${baseURL}empresas/obtener?id=${currentUser.IdEmpresa}`,
					{
						headers: {
							Authorization: `Bearer ${currentUser.access}`,
						},
					}
				);
				setEmpresas(response.data);
			} catch (error) {
			}
		} else {
			// Establecer empresas desde currentUser.empresas
			if (!currentUser.empresas) {
				return;
			}
			try {
				setEmpresas(currentUser.empresas);
			} catch (error) {
			}
		}
	};

	const fetchSucursales = async (nit) => {
		if (!nit) {
			setSucursales([]);
			return;
		}

		setLoading(true);
		try {
			const response = await axios.get(
				`${newAPIURL}/analytics/estructura-organizacional?nitemisor=${nit}`,
				{
					headers: {
						"Content-Type": "application/json",
					},
				}
			);


			if (response.data.success && response.data.data) {
				setSucursales(response.data.data.sucursales || []);
				// Limpiar el prefijo cuando se cambia la empresa
				setSelectedPrefijo("");
			} else {
				// Error in response - logging removed for clean console
				setSucursales([]);
			}
		} catch (error) {
			setSucursales([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const storedUser = localStorage.getItem("authUser");
		const currentUser = storedUser ? JSON.parse(storedUser) : null;
		if (currentUser) {
			const userGreeting = `Bienvenido, ${currentUser?.usuario ? `${currentUser.usuario.nombre} ${currentUser.usuario.apellido}` : "Usuario"}`;

			const breadcrumbItems = [
				{ title: "LS-DA", link: "#" },
				{ title: "Comprobantes", link: "#" },
				{ title: "Consultar Comprobantes", link: "#" },
			];
			props.setBreadcrumbItems(userGreeting, breadcrumbItems);
		}
		fetchTipoDocumento();
		fetchEmpresas(); // Llamar a la función para obtener las empresas
	}, [props]);

	// Auto-select empresa from header dropdown
	useEffect(() => {
		const storedEmpresa = localStorage.getItem("empresaSeleccionada")
		if (storedEmpresa) {
			try {
				const emp = JSON.parse(storedEmpresa)
				const nit = emp?.nit || emp?.identificacion || ""
				if (nit) {
					setNitemisor(nit)
					fetchSucursales(nit)
				}
			} catch (e) { /* ignore */ }
		}
	}, []) // eslint-disable-line

	// React to empresa change from header selector
	useEffect(() => {
		if (props.empresaSeleccionada) {
			const nit = props.empresaSeleccionada.nit || props.empresaSeleccionada.identificacion || ""
			if (nit && nit !== nitemisor) {
				setNitemisor(nit)
				setSelectedSucursal("")
				setSelectedPrefijo("")
				setFacturas([])
				setTotalDocs(0)
				setTotalPages(0)
				fetchSucursales(nit)
			}
		}
	}, [props.empresaSeleccionada]) // eslint-disable-line

	const handlePageClick = (data) => {
		const selectedPage = data.selected + 1;
		fetchFacturas(selectedPage);
	};

	const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
	const [loadingExport, setLoadingExport] = useState(false);

	const exportarComprobantes = async (formatoInicial = "csv") => {
		let formato = formatoInicial;
		setLoadingExport(true);

		try {
			// Construir los mismos parámetros que se usan en fetchFacturas
			const params = {};

			if (cufe && cufe.trim() !== "") {
				params.cufe = cufe;
			} else {
				if (nitemisor) params.nitemisor = nitemisor;
				if (fechaInicio)
					params.fecha_inicio = toLocalDateString(fechaInicio);
				if (fechaFin) params.fecha_fin = toLocalDateString(fechaFin);
				params.campo_fecha = campoFecha;
				if (tipoDocumento && tipoDocumento !== "TODOS LOS DOCUMENTOS")
					params.tipo_documento = tipoDocumento;
				if (selectedSucursal) params.sucursal = selectedSucursal;
				if (selectedPrefijo) params.prefijo = selectedPrefijo;
				if (nitReceptor) params.nitreceptor = nitReceptor;
				if (folio) params.folio = folio;
				if (tipoTercero === "publico") params.publicogeneral = 0;
				if (tipoTercero === "personalizadas") params.publicogeneral = 1;
				// Filtros delivery / datáfono (solo si no es "todas")
				if (filtroDelivery && filtroDelivery !== "todas") params.delivery = filtroDelivery;
				if (filtroDatafono && filtroDatafono !== "todas") params.datafono = filtroDatafono;
			}

			// Una sola llamada con responseType blob — timeout largo para archivos grandes
			const response = await axios.get(
				`${newAPIURL}/export/facturas-filtradas`,
				{
					params: params,
					responseType: "blob",
					timeout: 300000, // 5 minutos
					headers: {
						"Content-Type": "application/json",
					},
				}
			);

			// Verificar si la respuesta es JSON (error) en vez de archivo
			const contentType = response.headers["content-type"] || "";
			if (contentType.includes("application/json")) {
				const text = await response.data.text();
				const errorData = JSON.parse(text);
				if (errorData.success === false) {
					let errorMessage = `Error: ${errorData.error}`;
					if (errorData.total_count && errorData.max_allowed) {
						errorMessage += `\n\nRegistros encontrados: ${errorData.total_count.toLocaleString()}`;
						errorMessage += `\nMáximo permitido: ${errorData.max_allowed.toLocaleString()}`;
					}
					if (errorData.suggestion) {
						errorMessage += `\n\nSugerencia: ${errorData.suggestion}`;
					}
					alert(errorMessage);
					return;
				}
			}

			// Para archivos grandes + formato xlsx: verificar tamaño del blob
			const blobSizeMB = response.data.size / (1024 * 1024);
			if (formato === "xlsx" && blobSizeMB > 15) {
				const descargarCSV = window.confirm(
					`El archivo tiene ${Math.round(blobSizeMB)} MB y es demasiado grande para convertir a Excel en el navegador.\n\n¿Desea descargarlo como CSV en su lugar?`
				);
				if (descargarCSV) {
					formato = "csv"; // cambiar a CSV para descarga directa
				} else {
					return;
				}
			}

			// Descargar el archivo
			if (formato === "xlsx") {
				// Convertir CSV a Excel con formato profesional
				const csvText = await response.data.text();
				// raw:true → todas las celdas como string (sin conversión automática
				// a Date/number). Las columnas monetarias/numéricas las convertimos
				// explícitamente abajo, y las de fecha se quedan como string para
				// evitar cualquier desfase de zona horaria al renderizar.
				const workbook = XLSX.read(csvText, { type: "string", raw: true });
				const ws = workbook.Sheets[workbook.SheetNames[0]];

				// Renombrar hoja
				workbook.SheetNames[0] = "Comprobantes";
				workbook.Sheets["Comprobantes"] = ws;

				// Obtener rango de celdas
				const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
				const numRows = range.e.r + 1;

				// Modo ligero para archivos grandes (50k+ filas) — solo headers con estilo
				const isLargeFile = numRows > 50000;

				// Estilos
				const headerStyle = {
					fill: { fgColor: { rgb: "1B2A4A" } },
					font: { bold: true, color: { rgb: "FFFFFF" }, name: "Calibri", sz: 11 },
					alignment: { horizontal: "center", vertical: "center", wrapText: true },
					border: {
						top: { style: "thin", color: { rgb: "1B2A4A" } },
						bottom: { style: "thin", color: { rgb: "1B2A4A" } },
						left: { style: "thin", color: { rgb: "1B2A4A" } },
						right: { style: "thin", color: { rgb: "1B2A4A" } },
					},
				};

				// Detectar columnas numéricas/monetarias/fecha por nombre de header
				const moneyHeaders = new Set();
				const numHeaders = new Set();
				const dateHeaders = new Set();
				for (let c = 0; c <= range.e.c; c++) {
					const addr = XLSX.utils.encode_cell({ r: 0, c });
					const cell = ws[addr];
					if (cell && cell.v) {
						const h = String(cell.v).toLowerCase();
						// Normalizar tildes para detectar "emisión", "expedición", etc.
						const hNorm = h.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
						if (hNorm.includes("fecha") || hNorm.includes("vencimiento") || hNorm.includes("emision") || hNorm.includes("expedicion")) {
							dateHeaders.add(c);
						} else if (h.includes("subtotal") || h.includes("total") || h.includes("iva") || h.includes("impuesto") || h.includes("cargo") || h.includes("propina") || h.includes("inc") || h.includes("importe") || h.includes("valor")) {
							moneyHeaders.add(c);
						} else if (h.includes("cantidad") || h.includes("folio") || h.includes("numero")) {
							numHeaders.add(c);
						}
					}
				}

				// Traducir códigos DIAN a descripción en las columnas de receptor
				// (tipo_receptor / tipo_doc_receptor) para que el Excel muestre el texto,
				// no el código. Si el header no coincide, no se toca (degradación segura).
				for (let c = 0; c <= range.e.c; c++) {
					const headAddr = XLSX.utils.encode_cell({ r: 0, c });
					const headCell = ws[headAddr];
					if (!headCell || headCell.v == null) continue;
					const hn = String(headCell.v).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
					if (!hn.includes("receptor") || !hn.includes("tipo")) continue;
					const mapFn = hn.includes("doc") ? getTipoDocReceptorNombre : getTipoReceptorNombre;
					for (let r = 1; r <= range.e.r; r++) {
						const addr = XLSX.utils.encode_cell({ r, c });
						const cell = ws[addr];
						if (!cell || cell.v == null || cell.v === "") continue;
						cell.t = "s";
						cell.v = mapFn(String(cell.v).trim());
						if (cell.w !== undefined) delete cell.w;
					}
				}

				// Helper: devuelve la fecha como string "dd/mm/yyyy" sin convertir a
				// serial Excel ni Date. De esta forma Excel la renderiza tal cual y
				// no hay riesgo de desfase de zona horaria.
				const fechaToString = (v) => {
					if (v == null || v === "") return "";
					const s = String(v).trim();
					if (!s) return "";

					// "YYYY-MM-DD" o "YYYY-MM-DDTHH:..." → reformatear a dd/mm/yyyy
					let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
					if (m) return `${m[3]}/${m[2]}/${m[1]}`;

					// "DD/MM/YYYY" → ya está bien, devolver primeros 10 chars
					m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
					if (m) return `${m[1]}/${m[2]}/${m[3]}`;

					// Fallback: dejar lo que vino
					return s;
				};

				// Aplicar estilo a headers siempre
				for (let c = 0; c <= range.e.c; c++) {
					const addr = XLSX.utils.encode_cell({ r: 0, c });
					if (!ws[addr]) ws[addr] = { v: "", t: "s" };
					ws[addr].s = headerStyle;
				}

				if (!isLargeFile) {
					// Formato completo para archivos pequeños/medianos
					const borderThin = {
						top: { style: "thin", color: { rgb: "D0D0D0" } },
						bottom: { style: "thin", color: { rgb: "D0D0D0" } },
						left: { style: "thin", color: { rgb: "D0D0D0" } },
						right: { style: "thin", color: { rgb: "D0D0D0" } },
					};
					const evenRowFill = { fgColor: { rgb: "F2F6FC" } };
					const oddRowFill = { fgColor: { rgb: "FFFFFF" } };

					for (let r = 1; r <= range.e.r; r++) {
						const isEven = r % 2 === 0;
						for (let c = 0; c <= range.e.c; c++) {
							const addr = XLSX.utils.encode_cell({ r, c });
							if (!ws[addr]) ws[addr] = { v: "", t: "s" };
							const cell = ws[addr];

							cell.s = {
								fill: isEven ? evenRowFill : oddRowFill,
								font: { name: "Calibri", sz: 10, color: { rgb: "333333" } },
								border: borderThin,
								alignment: { vertical: "center" },
							};

							if (moneyHeaders.has(c) && cell.v != null && !isNaN(Number(cell.v))) {
								cell.t = "n";
								cell.v = Number(cell.v);
								cell.z = '#,##0';
								cell.s.alignment = { horizontal: "right", vertical: "center" };
							}
							if (numHeaders.has(c) && cell.v != null && !isNaN(Number(cell.v))) {
								cell.t = "n";
								cell.v = Number(cell.v);
								cell.z = '#,##0';
								cell.s.alignment = { horizontal: "center", vertical: "center" };
							}
							if (dateHeaders.has(c) && cell.v != null) {
								cell.t = "s";
								cell.v = fechaToString(cell.v);
								delete cell.z;
								cell.s.alignment = { horizontal: "center", vertical: "center" };
							}
						}
					}
				}

				// Formato de fecha también para archivos grandes (donde no se itera todas las celdas)
				if (isLargeFile && dateHeaders.size > 0) {
					for (let r = 1; r <= range.e.r; r++) {
						for (const c of dateHeaders) {
							const addr = XLSX.utils.encode_cell({ r, c });
							const cell = ws[addr];
							if (cell && cell.v != null) {
								cell.t = "s";
								cell.v = fechaToString(cell.v);
								delete cell.z;
							}
						}
					}
				}

				// Auto-ancho de columnas (muestrear solo primeras 200 filas para rendimiento)
				const colWidths = [];
				const sampleRows = Math.min(range.e.r, 200);
				for (let c = 0; c <= range.e.c; c++) {
					let maxLen = 8;
					for (let r = 0; r <= sampleRows; r++) {
						const addr = XLSX.utils.encode_cell({ r, c });
						const cell = ws[addr];
						if (cell && cell.v != null) {
							const len = String(cell.v).length;
							if (len > maxLen) maxLen = len;
						}
					}
					colWidths.push({ wch: Math.min(maxLen + 3, 45) });
				}
				ws["!cols"] = colWidths;

				// Congelar fila de headers
				ws["!freeze"] = { xSplit: 0, ySplit: 1 };

				const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
				const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;

				// Generar nombre de archivo dinámico
				let fileName = "comprobantes_filtrados";
				if (cufe && cufe.trim() !== "") {
					fileName += `_${cufe.substring(0, 8)}`;
				} else {
					if (nitemisor) fileName += `_${nitemisor}`;
					if (fechaInicio)
						fileName += `_${toLocalDateString(fechaInicio)}`;
					if (fechaFin) fileName += `_${toLocalDateString(fechaFin)}`;
				}
				fileName += ".xlsx";

				link.setAttribute("download", fileName);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			} else {
				// Descargar CSV directamente
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement("a");
				link.href = url;

				let fileName = "comprobantes_filtrados";
				if (cufe && cufe.trim() !== "") {
					fileName += `_${cufe.substring(0, 8)}`;
				} else {
					if (nitemisor) fileName += `_${nitemisor}`;
					if (fechaInicio)
						fileName += `_${toLocalDateString(fechaInicio)}`;
					if (fechaFin) fileName += `_${toLocalDateString(fechaFin)}`;
				}
				fileName += ".csv";

				link.setAttribute("download", fileName);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			}
		} catch (error) {
			// Verificar si es un error de respuesta HTTP con datos JSON
			if (error.response && error.response.data) {
				try {
					let errorData = error.response.data;
					if (error.response.data instanceof Blob) {
						const text = await error.response.data.text();
						errorData = JSON.parse(text);
					}
					if (errorData.success === false) {
						let errorMessage = `Error: ${errorData.error}`;
						if (errorData.total_count && errorData.max_allowed) {
							errorMessage += `\n\nRegistros encontrados: ${errorData.total_count.toLocaleString()}`;
							errorMessage += `\nMáximo permitido: ${errorData.max_allowed.toLocaleString()}`;
						}
						if (errorData.suggestion) {
							errorMessage += `\n\nSugerencia: ${errorData.suggestion}`;
						}
						alert(errorMessage);
						return;
					}
				} catch (parseError) { /* ignore parse error */ }
			}
			alert(
				"Error al exportar comprobantes. Por favor, verifique su conexión e inténtelo de nuevo."
			);
		} finally {
			setLoadingExport(false);
		}
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		// Si hay CUFE, solo validar que esté presente
		if (cufe && cufe.trim() !== "") {
			// Solo consultar por CUFE
			fetchFacturas(1);
			setCurrentPage(0);
		} else if (
			nitemisor &&
			selectedSucursal &&
			selectedPrefijo &&
			folio &&
			folio.trim() !== ""
		) {
			// Combinación especial: NIT Emisor + Sucursal + Prefijo + Folio (fechas opcionales)
			fetchFacturas(1);
			setCurrentPage(0);
		} else {
			// Validar que todos los filtros obligatorios estén presentes
			if (!nitemisor || !fechaInicio || !fechaFin) {
				alert(
					"Debe completar todos los campos obligatorios: NIT emisor, fecha de inicio y fecha fin para realizar la consulta"
				);
				return;
			}
			fetchFacturas(1);
			setCurrentPage(0);
		}
	};

	// Función para limpiar todos los filtros
	const limpiarFiltros = () => {
		setNitemisor("");
		setFechaRango([null, null]);
		setTipoDocumento("TODOS LOS DOCUMENTOS");
		setCufe("");
		setNitReceptor(""); // Limpiar nuevo parámetro
		setFolio(""); // Limpiar nuevo parámetro
		setSelectedSucursal("");
		setSelectedPrefijo("");
		setFacturas([]);
		setTotalPages(0);
		setTotalDocs(0);
		setCurrentPage(0);
		// Limpiar totales
		setTotalSubtotal(0);
		setTotalImpuestos(0);
		setTotalGeneral(0);
		setTotalIva(0);
		setTotalInc(0);
		setTotalOtrosImpuestos(0);
		setTotalCargoPropina(0);
	};

	const getDocumentoNombre = (code) => {
		const doc = documentos.find((doc) => doc.code === code);
		return doc ? doc.name : code;
	};

	const storedUser2 = localStorage.getItem("authUser");
	const currentUser2 = storedUser2 ? JSON.parse(storedUser2) : null;

	// Determinar si los campos están habilitados basado en el estado del CUFE o combinación especial
	const isCufeMode = !!(cufe && cufe.trim() !== "");
	const isSpecialFilterMode = !!(
		nitemisor &&
		selectedSucursal &&
		selectedPrefijo &&
		folio &&
		folio.trim() !== ""
	); // Nueva condición
	const isSpecialMode = isCufeMode; // Solo CUFE deshabilita otros campos
	const fechasObligatorias = !isCufeMode && !isSpecialFilterMode; // Fechas obligatorias solo si no es CUFE ni combinación especial
	const otrosCamposHabilitados = !isSpecialMode;

	// Calcular mensaje dinámicamente
	let mensajeFiltros = "Todos opcionales";
	if (isCufeMode) {
		mensajeFiltros = "Solo CUFE requerido";
	} else if (isSpecialFilterMode) {
		mensajeFiltros = "Filtros específicos (fechas opcionales)";
	} else if (fechasObligatorias) {
		mensajeFiltros = "* Campos obligatorios";
	}

	const empresaOptions = empresas.map((empresa) => {
		let value, label;

		if (currentUser2 && currentUser2.role === "SuperAdmin") {
			// Para SuperAdmin
			value = empresa.identificacion;
			label = empresa.nombre;
		} else {
			// Para otros roles
			value = empresa.idempresa.identificacion;
			label = empresa.idempresa.nombre;
		}

		return { value, label };
	});

	const sucursalOptions = [
		{ value: "", label: "Todos" },
		...sucursales.map((sucursal) => ({
			value: sucursal.sucursal,
			label: sucursal.sucursal,
		})),
	];

	const handleNitemisorChange = (selectedOption) => {
		setNitemisor(selectedOption ? selectedOption.value : "");
		setSelectedSucursal(""); // Resetear la sucursal seleccionada
		setSelectedPrefijo(""); // Resetear el prefijo seleccionado
		fetchSucursales(selectedOption ? selectedOption.value : "");
	};

	const handleSucursalChange = (selectedOption) => {
		setSelectedSucursal(selectedOption ? selectedOption.value : "");
		setSelectedPrefijo(""); // Reiniciar el prefijo seleccionado
		// Los prefijos ya están disponibles en la estructura, no necesitamos hacer otra llamada
	};

	// Generar opciones de prefijo basadas en la sucursal seleccionada
	const getPrefijoOptions = () => {
		if (!selectedSucursal) {
			return [{ value: "", label: "Todos" }];
		}

		const sucursal = sucursales.find((s) => s.sucursal === selectedSucursal);
		if (!sucursal) {
			return [{ value: "", label: "Todos" }];
		}

		return [
			{ value: "", label: "Todos" },
			...sucursal.prefijos.map((prefijo) => ({
				value: prefijo.split(" - ")[0], // Extraer solo el código del prefijo (ej: "212P")
				label: prefijo, // Mostrar el prefijo completo (ej: "212P - Factura Electrónica")
			})),
		];
	};

	const prefijoOptions = getPrefijoOptions();

	const correoEventos = correoTraceData.flatMap((item) =>
		(item.events || []).map((event) => ({ ...event, message_id: item.message_id }))
	);

	// Mapeo de colores por tipo de evento de correo (Postmark)
	const getEventColor = (recordType) => {
		const colors = {
			'Delivery': { bg: '#28a745', label: 'Entregado', icon: 'fas fa-check-circle' },
			'Open': { bg: '#17a2b8', label: 'Abierto', icon: 'fas fa-envelope-open' },
			'Click': { bg: '#6f42c1', label: 'Clic', icon: 'fas fa-mouse-pointer' },
			'Bounce': { bg: '#dc3545', label: 'Rebotado', icon: 'fas fa-exclamation-triangle' },
			'HardBounce': { bg: '#dc3545', label: 'Rebote Permanente', icon: 'fas fa-times-circle' },
			'SoftBounce': { bg: '#fd7e14', label: 'Rebote Temporal', icon: 'fas fa-exclamation-circle' },
			'SpamComplaint': { bg: '#6c757d', label: 'Spam', icon: 'fas fa-ban' },
			'Unsubscribe': { bg: '#ffc107', label: 'Desuscrito', icon: 'fas fa-user-minus' },
			'SubscriptionChange': { bg: '#20c997', label: 'Cambio Suscripción', icon: 'fas fa-user-edit' },
			'LinkClick': { bg: '#6f42c1', label: 'Clic en Enlace', icon: 'fas fa-link' },
			'Processed': { bg: '#007bff', label: 'Procesado', icon: 'fas fa-cog' },
			'Queued': { bg: '#6c757d', label: 'En Cola', icon: 'fas fa-clock' },
		};
		return colors[recordType] || { bg: '#6c757d', label: recordType || 'Desconocido', icon: 'fas fa-envelope' };
	};

	// Función para generar certificado PDF de trazabilidad de correo
	// Usa correoInboxFull (respuesta /a7/inbox/{cufe}/full) con info enriquecida:
	// documento, estado_actual, resumen, destinatarios[] (intentos+SES), historial_intentos[]
	const generarCertificadoCorreo = () => {
		if (!correoTarget) {
			alert("No hay un comprobante seleccionado.");
			return;
		}
		if (!correoInboxFull && correoEventos.length === 0) {
			alert("No hay datos de trazabilidad para generar el certificado.");
			return;
		}

		const full = correoInboxFull || {};
		const docData = full.documento || {};
		const estado = full.estado_actual || {};
		const resumen = full.resumen || {};
		const destinatarios = full.destinatarios || [];
		const historial = full.historial_intentos || [];

		const doc = new jsPDF();
		const pageWidth = doc.internal.pageSize.getWidth();
		const pageHeight = doc.internal.pageSize.getHeight();
		const margin = 15;
		let yPos = margin;
		let pageNum = 1;

		// Paleta corporativa
		const primary = [29, 53, 87];        // Azul oscuro
		const accent = [69, 123, 157];       // Azul medio
		const success = [42, 157, 143];      // Verde
		const warning = [231, 111, 81];      // Naranja
		const danger = [230, 57, 70];        // Rojo
		const info = [38, 198, 218];         // Cian
		const purple = [124, 58, 237];       // Púrpura
		const gray = [108, 117, 125];
		const lightBg = [248, 249, 250];

		const fmtCurrency = (v) => {
			if (v == null) return "—";
			return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(Number(v));
		};
		const fmtDate = (d) => {
			if (!d) return "—";
			const s = String(d);
			return s.length >= 10 ? s.substring(0, 19).replace("T", " ") : s;
		};

		const ensureSpace = (need) => {
			if (yPos + need > pageHeight - 18) {
				addFooter();
				doc.addPage();
				pageNum++;
				yPos = margin;
			}
		};

		const addFooter = () => {
			doc.setDrawColor(220, 220, 220);
			doc.setLineWidth(0.3);
			doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
			doc.setFont("helvetica", "bold");
			doc.setFontSize(8);
			doc.setTextColor(...primary);
			doc.text("LS-DA · Softlogy", margin, pageHeight - 7);
			doc.setFont("helvetica", "normal");
			doc.setFontSize(7);
			doc.setTextColor(...gray);
			doc.text("Sistema de Facturación Electrónica · Trazabilidad SES", margin + 30, pageHeight - 7);
			doc.text(`Página ${pageNum}`, pageWidth - margin, pageHeight - 7, { align: "right" });
		};

		const sectionTitle = (title, icon = "") => {
			ensureSpace(12);
			doc.setFillColor(...primary);
			doc.rect(margin, yPos, pageWidth - margin * 2, 7, "F");
			doc.setTextColor(255, 255, 255);
			doc.setFont("helvetica", "bold");
			doc.setFontSize(10);
			doc.text(`${icon ? icon + "  " : ""}${title}`, margin + 3, yPos + 5);
			yPos += 10;
			doc.setTextColor(60, 60, 60);
		};

		// ============ CABECERA EN BANNER ============
		doc.setFillColor(...primary);
		doc.rect(0, 0, pageWidth, 38, "F");
		// Acento
		doc.setFillColor(...success);
		doc.rect(0, 38, pageWidth, 2, "F");

		doc.setTextColor(255, 255, 255);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(20);
		doc.text("CERTIFICADO DE TRAZABILIDAD", pageWidth / 2, 16, { align: "center" });
		doc.setFontSize(11);
		doc.setFont("helvetica", "normal");
		doc.text("Envío y entrega de documento electrónico", pageWidth / 2, 24, { align: "center" });
		doc.setFontSize(8);
		const fechaGen = new Date().toLocaleString("es-CO", { dateStyle: "long", timeStyle: "medium" });
		doc.text(`Emitido: ${fechaGen}`, pageWidth / 2, 32, { align: "center" });

		yPos = 46;
		doc.setTextColor(60, 60, 60);

		// ============ 1. INFORMACIÓN DEL DOCUMENTO ============
		sectionTitle("INFORMACIÓN DEL DOCUMENTO");

		const docRows = [
			["Tipo de documento", docData.tipo_documento || "—"],
			["Prefijo · Folio", `${docData.prefijo || ""} · ${docData.folio || ""}`.trim() || `${correoTarget.prefijo || ""}${correoTarget.folio || ""}`],
			["Fecha del documento", docData.fecha_documento ? docData.fecha_documento.substring(0, 10) : "—"],
			["Valor total", fmtCurrency(docData.total_factura)],
			["Emisor", `${docData.nombre_emisor || correoTarget.nitemisor || "—"}`],
			["NIT emisor", docData.nitemisor || correoTarget.nitemisor || "—"],
			["Sucursal", docData.sucursal || "—"],
			["Receptor", docData.nombre_receptor || "—"],
			["NIT receptor", docData.nit_receptor || "—"],
			["Tipo PDF", docData.tipopdf || "—"],
		];

		doc.autoTable({
			startY: yPos,
			body: docRows,
			theme: "plain",
			styles: { fontSize: 8.5, cellPadding: 2.2, valign: "middle" },
			columnStyles: {
				0: { cellWidth: 50, fontStyle: "bold", textColor: primary, fillColor: lightBg },
				1: { cellWidth: "auto", textColor: [40, 40, 40] },
			},
			margin: { left: margin, right: margin },
		});
		yPos = doc.lastAutoTable.finalY + 2;

		// CUFE en caja propia (puede ser muy largo)
		ensureSpace(15);
		doc.setFillColor(...lightBg);
		doc.rect(margin, yPos, pageWidth - margin * 2, 12, "F");
		doc.setFont("helvetica", "bold");
		doc.setFontSize(8.5);
		doc.setTextColor(...primary);
		doc.text("CUFE:", margin + 2, yPos + 4.5);
		doc.setFont("courier", "normal");
		doc.setFontSize(7);
		doc.setTextColor(40, 40, 40);
		const cufeStr = correoTarget.cufe || full.cufe || "—";
		const cufeLines = doc.splitTextToSize(cufeStr, pageWidth - margin * 2 - 18);
		cufeLines.forEach((ln, idx) => {
			doc.text(ln, margin + 15, yPos + 4.5 + (idx * 3.5));
		});
		yPos += Math.max(12, cufeLines.length * 3.5 + 5);

		// ============ 2. ESTADO ACTUAL ============
		sectionTitle("ESTADO ACTUAL DEL ENVÍO");

		const estadoStr = String(estado.estado || "").toLowerCase();
		const estadoColor = estadoStr.includes("sent") ? success
			: (estadoStr.includes("fail") || estadoStr.includes("bounce")) ? danger
			: estadoStr.includes("processing") ? accent
			: gray;
		const estadoLabel = (estado.estado || "—").toUpperCase();

		// Chip grande
		ensureSpace(20);
		doc.setFillColor(...estadoColor);
		doc.roundedRect(margin, yPos, 60, 14, 2, 2, "F");
		doc.setTextColor(255, 255, 255);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(11);
		doc.text(estadoLabel, margin + 30, yPos + 9, { align: "center" });
		doc.setTextColor(60, 60, 60);

		// Metadata al lado del chip
		doc.setFont("helvetica", "normal");
		doc.setFontSize(8.5);
		const estRight = margin + 65;
		doc.setFont("helvetica", "bold");
		doc.text("Última etapa:", estRight, yPos + 4);
		doc.setFont("helvetica", "normal");
		doc.text(estado.ultima_etapa || "—", estRight + 28, yPos + 4);
		doc.setFont("helvetica", "bold");
		doc.text("Total intentos:", estRight, yPos + 9);
		doc.setFont("helvetica", "normal");
		doc.text(String(estado.total_intentos ?? 0), estRight + 28, yPos + 9);
		doc.setFont("helvetica", "bold");
		doc.text("Fuente último:", estRight, yPos + 14);
		doc.setFont("helvetica", "normal");
		doc.text(estado.fuente_ultimo_intento || "—", estRight + 28, yPos + 14);
		yPos += 18;

		const estadoRows = [
			["Primer intento", fmtDate(estado.primer_intento_at)],
			["Último intento", fmtDate(estado.ultimo_intento_at)],
			["Enviado en", estado.enviado_at && !String(estado.enviado_at).startsWith("1970") ? fmtDate(estado.enviado_at) : "—"],
			["Correo receptor", estado.mail_receptor_raw || "—"],
			["Destinatarios totales", String(estado.destinatarios_total ?? 0)],
			["Destinatarios OK", String(estado.destinatarios_ok ?? 0)],
			["Destinatarios fallidos", String(estado.destinatarios_fail ?? 0)],
			["Tipo bounce", estado.bounce_type || "—"],
			["Último error", estado.ultimo_error || "—"],
		];
		doc.autoTable({
			startY: yPos,
			body: estadoRows,
			theme: "plain",
			styles: { fontSize: 8, cellPadding: 1.8 },
			columnStyles: {
				0: { cellWidth: 50, fontStyle: "bold", textColor: primary, fillColor: lightBg },
				1: { cellWidth: "auto" },
			},
			margin: { left: margin, right: margin },
		});
		yPos = doc.lastAutoTable.finalY + 5;

		// ============ 3. KPIs DEL RESUMEN ============
		sectionTitle("RESUMEN DE TRAZABILIDAD");

		const kpis = [
			{ label: "Destinatarios", value: resumen.total_destinatarios ?? destinatarios.length, color: accent },
			{ label: "Enviados a SES", value: resumen.destinatarios_enviados_a_ses ?? 0, color: info },
			{ label: "Fallos pre-SES", value: resumen.destinatarios_fallaron_antes_de_ses ?? 0, color: danger },
			{ label: "Aperturas", value: resumen.destinatarios_que_abrieron ?? 0, color: success },
			{ label: "Clics", value: resumen.destinatarios_que_hicieron_click ?? 0, color: purple },
			{ label: "Bounces SES", value: resumen.destinatarios_con_bounce_real_ses ?? 0, color: warning },
		];

		const kpiCols = 6;
		const kpiW = (pageWidth - margin * 2 - (kpiCols - 1) * 3) / kpiCols;
		const kpiH = 18;
		ensureSpace(kpiH + 4);
		kpis.forEach((kpi, idx) => {
			const x = margin + idx * (kpiW + 3);
			doc.setFillColor(...kpi.color);
			doc.roundedRect(x, yPos, kpiW, kpiH, 2, 2, "F");
			doc.setTextColor(255, 255, 255);
			doc.setFont("helvetica", "bold");
			doc.setFontSize(13);
			doc.text(String(kpi.value), x + kpiW / 2, yPos + 9, { align: "center" });
			doc.setFont("helvetica", "normal");
			doc.setFontSize(6.5);
			doc.text(kpi.label, x + kpiW / 2, yPos + 15, { align: "center" });
		});
		yPos += kpiH + 6;
		doc.setTextColor(60, 60, 60);

		// ============ 4. DETALLE POR DESTINATARIO ============
		if (destinatarios.length > 0) {
			sectionTitle("DETALLE POR DESTINATARIO");

			destinatarios.forEach((d, idx) => {
				ensureSpace(20);
				// Banda con email + estado
				const okDest = String(d.estado_envio_actual || "").toLowerCase() === "sent";
				const colorDest = okDest ? success : danger;
				doc.setFillColor(...colorDest);
				doc.rect(margin, yPos, 3, 8, "F");
				doc.setFillColor(...lightBg);
				doc.rect(margin + 3, yPos, pageWidth - margin * 2 - 3, 8, "F");
				doc.setFont("helvetica", "bold");
				doc.setFontSize(9);
				doc.setTextColor(...primary);
				doc.text(`${idx + 1}. ${d.email || "—"}`, margin + 6, yPos + 5.5);
				doc.setFont("helvetica", "normal");
				doc.setFontSize(7.5);
				doc.setTextColor(...colorDest);
				doc.text(`[${(d.estado_envio_actual || "—").toUpperCase()}]`,
					pageWidth - margin - 2, yPos + 5.5, { align: "right" });
				yPos += 9;
				doc.setTextColor(60, 60, 60);

				// Message ID
				if (d.message_id) {
					ensureSpace(4);
					doc.setFont("courier", "normal");
					doc.setFontSize(6.5);
					doc.setTextColor(...gray);
					const msgLines = doc.splitTextToSize(`MessageID: ${d.message_id}`, pageWidth - margin * 2);
					msgLines.forEach((ln) => {
						doc.text(ln, margin + 3, yPos + 3);
						yPos += 3.2;
					});
					yPos += 1;
				}

				// Tabla de intentos de envío
				if ((d.intentos_envio || []).length > 0) {
					ensureSpace(10);
					doc.setFont("helvetica", "bold");
					doc.setFontSize(8);
					doc.setTextColor(...primary);
					doc.text("Intentos de envío:", margin + 3, yPos + 3);
					yPos += 4;

					const intentosBody = (d.intentos_envio || []).map((att) => {
						const dur = att.duration_ms || {};
						return [
							fmtDate(att.event_time),
							att.source || "—",
							(att.status || "—").toUpperCase(),
							att.http_status_code != null ? String(att.http_status_code) : "—",
							att.error_stage || "—",
							att.error_message ? (att.error_message.length > 40 ? att.error_message.substring(0, 40) + "…" : att.error_message) : "—",
							`${dur.total ?? 0}ms`,
							`${dur.download ?? 0}/${dur.pdf ?? 0}/${dur.email ?? 0}`,
						];
					});

					doc.autoTable({
						startY: yPos,
						head: [["Fecha/Hora", "Fuente", "Estado", "HTTP", "Etapa error", "Mensaje error", "Total", "DL/PDF/Mail ms"]],
						body: intentosBody,
						theme: "grid",
						styles: { fontSize: 6.5, cellPadding: 1.5 },
						headStyles: { fillColor: accent, textColor: [255, 255, 255], fontSize: 6.5, halign: "center", fontStyle: "bold" },
						columnStyles: {
							0: { cellWidth: 30 },
							1: { cellWidth: 16 },
							2: { cellWidth: 14, halign: "center" },
							3: { cellWidth: 12, halign: "center" },
							4: { cellWidth: 22 },
							5: { cellWidth: "auto" },
							6: { cellWidth: 15, halign: "right" },
							7: { cellWidth: 28, halign: "center" },
						},
						didParseCell: function (data) {
							if (data.section === "body" && data.column.index === 2) {
								const v = (data.cell.text[0] || "").toLowerCase();
								if (v.includes("sent")) data.cell.styles.fillColor = [212, 237, 218];
								else if (v.includes("fail") || v.includes("error")) data.cell.styles.fillColor = [248, 215, 218];
							}
						},
						margin: { left: margin, right: margin },
					});
					yPos = doc.lastAutoTable.finalY + 2;
				}

				// Tabla de eventos SES (Open/Click/Bounce)
				if ((d.eventos_ses || []).length > 0) {
					ensureSpace(10);
					doc.setFont("helvetica", "bold");
					doc.setFontSize(8);
					doc.setTextColor(...primary);
					doc.text("Eventos SES (Open/Click/Bounce):", margin + 3, yPos + 3);
					yPos += 4;

					const sesBody = (d.eventos_ses || []).map((e) => [
						fmtDate(e.event_time),
						e.tipo || "—",
						e.subtipo || "—",
						e.ip_address || "—",
						e.user_agent ? (e.user_agent.length > 60 ? e.user_agent.substring(0, 60) + "…" : e.user_agent) : "—",
					]);

					doc.autoTable({
						startY: yPos,
						head: [["Fecha/Hora", "Tipo", "Subtipo", "IP", "User Agent"]],
						body: sesBody,
						theme: "grid",
						styles: { fontSize: 6.5, cellPadding: 1.5 },
						headStyles: { fillColor: success, textColor: [255, 255, 255], fontSize: 6.5, halign: "center", fontStyle: "bold" },
						columnStyles: {
							0: { cellWidth: 32 },
							1: { cellWidth: 18, halign: "center" },
							2: { cellWidth: 18, halign: "center" },
							3: { cellWidth: 26 },
							4: { cellWidth: "auto" },
						},
						didParseCell: function (data) {
							if (data.section === "body" && data.column.index === 1) {
								const v = (data.cell.text[0] || "").toLowerCase();
								if (v === "open") data.cell.styles.fillColor = [212, 237, 218];
								else if (v === "click") data.cell.styles.fillColor = [232, 222, 248];
								else if (v.includes("bounce")) data.cell.styles.fillColor = [248, 215, 218];
							}
						},
						margin: { left: margin, right: margin },
					});
					yPos = doc.lastAutoTable.finalY + 4;
				}
				yPos += 1;
			});
		}

		// ============ 5. HISTORIAL DE INTENTOS (VERSIONES) ============
		if (historial.length > 0) {
			sectionTitle("HISTORIAL DE VERSIONES DEL ENVÍO");

			const histBody = historial.map((h) => [
				fmtDate(h.ultimo_intento_at),
				h.estado || "—",
				h.ultima_etapa || "—",
				String(h.total_intentos ?? 0),
				String(h.destinatarios_ok ?? 0),
				String(h.destinatarios_fail ?? 0),
				h.fuente_ultimo_intento || "—",
				h.enviado_at && !String(h.enviado_at).startsWith("1970") ? fmtDate(h.enviado_at) : "—",
				h.ultimo_error ? (h.ultimo_error.length > 30 ? h.ultimo_error.substring(0, 30) + "…" : h.ultimo_error) : "—",
			]);

			doc.autoTable({
				startY: yPos,
				head: [["Fecha", "Estado", "Etapa", "Intentos", "OK", "Fail", "Fuente", "Enviado", "Error"]],
				body: histBody,
				theme: "striped",
				styles: { fontSize: 6.5, cellPadding: 1.5 },
				headStyles: { fillColor: primary, textColor: [255, 255, 255], fontSize: 6.5, halign: "center", fontStyle: "bold" },
				columnStyles: {
					0: { cellWidth: 28 },
					1: { cellWidth: 20, halign: "center" },
					2: { cellWidth: 18, halign: "center" },
					3: { cellWidth: 14, halign: "center" },
					4: { cellWidth: 10, halign: "center" },
					5: { cellWidth: 10, halign: "center" },
					6: { cellWidth: 16, halign: "center" },
					7: { cellWidth: 28 },
					8: { cellWidth: "auto" },
				},
				didParseCell: function (data) {
					if (data.section === "body" && data.column.index === 1) {
						const v = (data.cell.text[0] || "").toLowerCase();
						if (v.includes("sent")) data.cell.styles.textColor = success;
						else if (v.includes("fail")) data.cell.styles.textColor = danger;
						else if (v.includes("processing")) data.cell.styles.textColor = accent;
					}
				},
				margin: { left: margin, right: margin },
			});
			yPos = doc.lastAutoTable.finalY + 6;
		}

		// ============ 6. FIRMA DIGITAL ============
		ensureSpace(50);
		// Generar cadena digital basada en TODOS los datos significativos
		const dataToHash = JSON.stringify({
			cufe: correoTarget.cufe || full.cufe,
			emisor: docData.nitemisor || correoTarget.nitemisor,
			doc: `${docData.prefijo || ""}${docData.folio || ""}`,
			estado: estado.estado,
			intentos: estado.total_intentos,
			destinatarios_ok: estado.destinatarios_ok,
			ultima: estado.ultimo_intento_at,
			timestamp: new Date().toISOString(),
		});
		const cadenaBase64 = btoa(unescape(encodeURIComponent(dataToHash)));
		const maxChars = 95;
		const cadenaLines = [];
		for (let i = 0; i < cadenaBase64.length; i += maxChars) {
			cadenaLines.push(cadenaBase64.substring(i, i + maxChars));
		}

		const firmaH = 18 + (cadenaLines.length * 3) + 6;
		ensureSpace(firmaH);
		doc.setFillColor(252, 252, 253);
		doc.setDrawColor(200, 200, 200);
		doc.setLineWidth(0.4);
		doc.roundedRect(margin, yPos, pageWidth - margin * 2, firmaH, 2, 2, "FD");
		doc.setFillColor(...success);
		doc.rect(margin, yPos, 3, firmaH, "F");

		doc.setFillColor(...success);
		doc.circle(margin + 12, yPos + 9, 4, "F");
		doc.setTextColor(255, 255, 255);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(7);
		doc.text("OK", margin + 12, yPos + 10.3, { align: "center" });

		doc.setTextColor(...primary);
		doc.setFontSize(9);
		doc.setFont("helvetica", "bold");
		doc.text("FIRMA DIGITAL DE CERTIFICACIÓN", margin + 20, yPos + 7);

		doc.setFont("helvetica", "normal");
		doc.setFontSize(7);
		doc.setTextColor(100, 100, 100);
		const fechaEmision = new Date().toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "medium" });
		doc.text(`Emitido: ${fechaEmision}  ·  Emisor: LS-DA Softlogy  ·  Algoritmo: SHA-256 (Base64)`, margin + 20, yPos + 12);

		doc.setFontSize(6.5);
		doc.setTextColor(80, 80, 80);
		doc.text("Cadena Digital:", margin + 8, yPos + 18);

		doc.setFillColor(243, 244, 246);
		doc.roundedRect(margin + 8, yPos + 19.5, pageWidth - margin * 2 - 16, cadenaLines.length * 3 + 2, 1, 1, "F");
		doc.setFont("courier", "bold");
		doc.setFontSize(5.5);
		doc.setTextColor(40, 40, 40);
		cadenaLines.forEach((ln, i) => {
			doc.text(ln, margin + 10, yPos + 22.5 + i * 3);
		});
		yPos += firmaH + 4;

		// ============ NOTA LEGAL ============
		ensureSpace(15);
		doc.setFont("helvetica", "italic");
		doc.setFontSize(6.5);
		doc.setTextColor(120, 120, 120);
		const nota = "Este certificado es generado automáticamente por LS-DA en base a los registros del servicio de notificación (AWS SES) y del listener interno. " +
			"La cadena digital incorpora un hash de los datos significativos del envío (CUFE, emisor, documento, intentos, fecha) y permite verificar la integridad del registro. " +
			"Constituye un soporte fehaciente de la trazabilidad del envío electrónico hacia los destinatarios listados.";
		const splitNota = doc.splitTextToSize(nota, pageWidth - margin * 2);
		doc.text(splitNota, margin, yPos);

		// Pie de última página
		addFooter();

		const docLabel = `${docData.prefijo || correoTarget.prefijo || ""}${docData.folio || correoTarget.folio || ""}`;
		const fileName = `Certificado_Trazabilidad_${docLabel}_${toLocalDateString(new Date())}.pdf`;
		doc.save(fileName);
	};

		const customStyles = {
		control: (provided) => ({
			...provided,
			zIndex: 1000,
		}),
		menu: (provided) => ({
			...provided,
			zIndex: 1050,
		}),
	};

	return (
		<React.Fragment>
			<Row>
				<Col className="col-12">
					<Card>
						<CardBody>
							<div className="d-flex justify-content-between align-items-center mb-3">
								<CardTitle className="h4">Facturas</CardTitle>
								<Button color="danger" onClick={() => navigate("/portal")}>
									Salir
								</Button>
							</div>
							<p className="card-title-desc">
								Visualice y gestione las facturas emitidas.
							</p>
							<hr />

							{/* Sección de Filtros de Búsqueda */}
							<Card className="border-0 shadow-sm filter-card">
								<CardBody className="bg-light">
									<div className="d-flex align-items-center mb-4">
										<div className="flex-shrink-0">
											<div className="avatar-sm rounded-circle bg-primary d-flex align-items-center justify-content-center">
												<i className="mdi mdi-filter-variant text-white font-size-16"></i>
											</div>
										</div>
										<div className="flex-grow-1 ms-3">
											<h5 className="mb-1">Parámetros de Búsqueda</h5>
											<p className="text-muted mb-0">
												Configure los filtros para consultar los comprobantes
												electrónicos
											</p>
										</div>
									</div>

									<Form onSubmit={handleSubmit}>
										{/* Sección de Filtros - Compacta */}
										<Card className="border border-primary border-opacity-25 mb-3 filter-card">
											<CardBody className="p-3">
												<div className="d-flex align-items-center justify-content-between mb-2">
													<div className="d-flex align-items-center">
														<i className="mdi mdi-filter-variant me-2 text-primary"></i>
														<h6 className="mb-0 text-primary">
															Filtros de Comprobantes
														</h6>
													</div>
													<small className="text-muted">{mensajeFiltros}</small>
												</div>

												{/* Primera fila - Filtros principales */}
												<Row className="g-2 mb-2">
													<Col lg={3} md={6}>
														<FormGroup className="mb-2">
															<Label
																className="form-label fw-medium small">
																<i className="mdi mdi-domain me-1 text-primary"></i>
																NIT Emisor
															</Label>
															<div style={{ padding: "6px 12px", background: "#f0f4ff", borderRadius: 6, border: "1px solid #c3cbe4", fontSize: 13, color: "#343a40", fontWeight: 600, minHeight: 38, display: "flex", alignItems: "center" }}>
																{nitemisor ? (
																	<><i className="mdi mdi-check-circle text-success me-1"></i>{(() => {
																		try {
																			const emp = JSON.parse(localStorage.getItem("empresaSeleccionada") || "{}")
																			return `${emp.nombre || ""} - ${nitemisor}`
																		} catch (e) { return nitemisor }
																	})()}</>
																) : (
																	<span style={{ color: "#adb5bd" }}><i className="mdi mdi-alert-circle-outline me-1"></i>No hay empresa seleccionada</span>
																)}
															</div>
														</FormGroup>
													</Col>

													<Col lg={2} md={6}>
														<FormGroup className="mb-2">
															<Label
																htmlFor="fechaInicio"
																className="form-label fw-medium small">
																<i className="mdi mdi-calendar-start me-1 text-success"></i>
																Fecha Inicio{" "}
																{fechasObligatorias && (
																	<span className="text-danger">*</span>
																)}
															</Label>
															<DatePicker
																selected={fechaInicio}
																onChange={(date) =>
																	setFechaRango([date, fechaFin])
																}
																selectsStart
																startDate={fechaInicio}
																endDate={fechaFin}
																maxDate={new Date()}
																className="form-control form-control-sm"
																placeholderText="Fecha inicio"
																dateFormat="dd/MM/yyyy"
																locale="es"
																disabled={isSpecialMode}
																required={fechasObligatorias}
																showMonthDropdown
																showYearDropdown
																dropdownMode="select"
																withPortal
																portalId="date-picker-portal"
																popperClassName="date-picker-popper"
															/>
														</FormGroup>
													</Col>

													<Col lg={2} md={6}>
														<FormGroup className="mb-2">
															<Label
																htmlFor="fechaFin"
																className="form-label fw-medium small">
																<i className="mdi mdi-calendar-end me-1 text-success"></i>
																Fecha Fin{" "}
																{fechasObligatorias && (
																	<span className="text-danger">*</span>
																)}
															</Label>
															<DatePicker
																selected={fechaFin}
																onChange={(date) =>
																	setFechaRango([fechaInicio, date])
																}
																selectsEnd
																startDate={fechaInicio}
																endDate={fechaFin}
																minDate={fechaInicio}
																maxDate={new Date()}
																className="form-control form-control-sm"
																placeholderText="Fecha fin"
																dateFormat="dd/MM/yyyy"
																locale="es"
																disabled={isSpecialMode}
																required={fechasObligatorias}
																showMonthDropdown
																showYearDropdown
																dropdownMode="select"
																withPortal
																portalId="date-picker-portal"
																popperClassName="date-picker-popper"
															/>
														</FormGroup>
													</Col>

													<Col lg={3} md={6}>
														<FormGroup className="mb-2">
															<Label
																for="cufe"
																className="form-label fw-medium small">
																<i className="mdi mdi-key me-1 text-success"></i>
																CUFE
																<UncontrolledTooltip
																	target="cufe"
																	placement="top"
																	transition={{ timeout: 250 }}>
																	Código Único de Facturación Electrónica
																</UncontrolledTooltip>
															</Label>
															<Input
																type="text"
																id="cufe"
																value={cufe}
																onChange={(e) => setCufe(e.target.value)}
																placeholder="Ingrese CUFE..."
																className="form-control form-control-sm"
																style={{
																	borderColor: "#ced4da",
																	transition:
																		"border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
																}}
																onFocus={(e) => {
																	e.target.style.borderColor = "#28a745";
																	e.target.style.boxShadow =
																		"0 0 0 0.2rem rgba(40, 167, 69, 0.25)";
																}}
																onBlur={(e) => {
																	e.target.style.borderColor = "#ced4da";
																	e.target.style.boxShadow = "none";
																}}
															/>
														</FormGroup>
													</Col>

													<Col lg={2} md={6}>
														<FormGroup className="mb-2">
															<Label
																for="tipoDocumento"
																className="form-label fw-medium small">
																<i className="mdi mdi-file-document me-1 text-info"></i>
																Tipo Documento
															</Label>
															<Input
																type="select"
																id="tipoDocumento"
																value={tipoDocumento}
																onChange={(e) =>
																	setTipoDocumento(e.target.value)
																}
																disabled={isSpecialMode}
																className="form-select form-select-sm"
																style={{
																	borderColor: "#ced4da",
																	transition:
																		"border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
																}}
																onFocus={(e) => {
																	e.target.style.borderColor = "#17a2b8";
																	e.target.style.boxShadow =
																		"0 0 0 0.2rem rgba(23, 162, 184, 0.25)";
																}}
																onBlur={(e) => {
																	e.target.style.borderColor = "#ced4da";
																	e.target.style.boxShadow = "none";
																}}>
																<option value="TODOS LOS DOCUMENTOS">
																	Todos
																</option>
																{documentos.map((type) => (
																	<option key={type.code} value={type.code}>
																		{type.name}
																	</option>
																))}
															</Input>
														</FormGroup>
													</Col>

												<Col lg={2} md={6}>
													<FormGroup className="mb-2">
														<Label
															for="campoFecha"
															className="form-label fw-medium small"
															title="Define qué fecha del documento se usa para el filtro de rango">
															<i className="mdi mdi-calendar-question me-1 text-info"></i>
															Campo Fecha
														</Label>
														<Input
															type="select"
															id="campoFecha"
															value={campoFecha}
															onChange={(e) => setCampoFecha(e.target.value)}
															disabled={isSpecialMode}
															className="form-select form-select-sm"
															style={{
																borderColor: "#ced4da",
																transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
															}}
															onFocus={(e) => {
																e.target.style.borderColor = "#17a2b8";
																e.target.style.boxShadow = "0 0 0 0.2rem rgba(23, 162, 184, 0.25)";
															}}
															onBlur={(e) => {
																e.target.style.borderColor = "#ced4da";
																e.target.style.boxShadow = "none";
															}}>
															<option value="fecha">Fecha emisión</option>
															<option value="fechageneracion">Fecha transmisión</option>
														</Input>
													</FormGroup>
												</Col>
												</Row>

												{fechaInicio && fechaFin && (
													<div
														className="alert alert-success p-2 mb-2"
														role="alert">
														<div className="d-flex align-items-center justify-content-between">
															<div className="d-flex align-items-center">
																<i className="mdi mdi-check-circle text-success me-2"></i>
																<small>
																	<strong>✅ Período:</strong>
																	{fechaInicio.toLocaleDateString(
																		"es-ES"
																	)} - {fechaFin.toLocaleDateString("es-ES")}(
																	{Math.ceil(
																		(fechaFin - fechaInicio) /
																		(1000 * 60 * 60 * 24)
																	) + 1}{" "}
																	días)
																</small>
															</div>
															<button
																type="button"
																className="btn btn-link btn-sm p-0 text-danger"
																onClick={() => setFechaRango([null, null])}
																style={{
																	fontSize: "12px",
																	textDecoration: "none",
																}}>
																<i className="mdi mdi-close-circle"></i>
															</button>
														</div>
													</div>
												)}

												{/* Separador compacto para filtros opcionales */}
												<div className="d-flex align-items-center mb-2 mt-2">
													<div className="flex-grow-1">
														<hr className="my-1" />
													</div>
													<div className="px-2">
														<small className="text-muted fw-medium">
															<i className="mdi mdi-tune me-1 text-info"></i>
															Filtros Opcionales
														</small>
													</div>
													<div className="flex-grow-1">
														<hr className="my-1" />
													</div>
												</div>

												{/* Segunda fila - Filtros adicionales compactos */}
												<Row className="g-2 mb-2">
													<Col lg={2} md={6}>
														<FormGroup className="mb-2">
															<Label
																for="sucursales"
																className="form-label fw-medium small">
																<i className="mdi mdi-office-building me-1 text-warning"></i>
																Sucursales
															</Label>
															<Select
																id="sucursales"
																value={sucursalOptions.find(
																	(option) => option.value === selectedSucursal
																)}
																onChange={handleSucursalChange}
																options={sucursalOptions}
																classNamePrefix="select2-selection"
																placeholder="Todas..."
																isClearable
																isDisabled={isSpecialMode}
																styles={{
																	...customStyles,
																	control: (provided, state) => ({
																		...provided,
																		minHeight: "38px",
																		borderColor: state.isFocused
																			? "#ffc107"
																			: "#ced4da",
																		boxShadow: state.isFocused
																			? "0 0 0 0.2rem rgba(255, 193, 7, 0.25)"
																			: "none",
																		"&:hover": {
																			borderColor: "#ffc107",
																		},
																	}),
																}}
															/>
														</FormGroup>
													</Col>
													<Col lg={2} md={6}>
														<FormGroup className="mb-2">
															<Label
																for="tipoTercero"
																className="form-label fw-medium small">
																<i className="mdi mdi-account-multiple me-1 text-info"></i>
																Tipo de Tercero
															</Label>
															<Input
																type="select"
																id="tipoTercero"
																value={tipoTercero}
																onChange={(e) => setTipoTercero(e.target.value)}
																disabled={isSpecialMode}
																className="form-select form-select-sm"
															>
																<option value="todos">Todos</option>
																<option value="publico">Publico General</option>
																<option value="personalizadas">Personalizadas</option>
															</Input>
														</FormGroup>
													</Col>

													<Col lg={2} md={6}>
														<FormGroup className="mb-2">
															<Label
																for="filtroDelivery"
																className="form-label fw-medium small">
																<i className="mdi mdi-moped me-1 text-success"></i>
																Delivery
															</Label>
															<Input
																type="select"
																id="filtroDelivery"
																value={filtroDelivery}
																onChange={(e) => setFiltroDelivery(e.target.value)}
																disabled={isSpecialMode}
																className="form-select form-select-sm"
															>
																<option value="todas">Todas</option>
																<option value="con">Con delivery</option>
																<option value="sin">Sin delivery</option>
															</Input>
														</FormGroup>
													</Col>

													<Col lg={2} md={6}>
														<FormGroup className="mb-2">
															<Label
																for="filtroDatafono"
																className="form-label fw-medium small">
																<i className="mdi mdi-credit-card-check-outline me-1 text-primary"></i>
																Datáfono
															</Label>
															<Input
																type="select"
																id="filtroDatafono"
																value={filtroDatafono}
																onChange={(e) => setFiltroDatafono(e.target.value)}
																disabled={isSpecialMode}
																className="form-select form-select-sm"
															>
																<option value="todas">Todas</option>
																<option value="con">Con datáfono</option>
																<option value="sin">Sin datáfono</option>
															</Input>
														</FormGroup>
													</Col>

													<Col lg={2} md={6}>
														<FormGroup className="mb-2">
															<Label
																for="prefijo"
																className="form-label fw-medium small">
																<i className="mdi mdi-tag me-1 text-secondary"></i>
																Prefijo
															</Label>
															<Select
																id="prefijo"
																value={prefijoOptions.find(
																	(option) => option.value === selectedPrefijo
																)}
																onChange={(selectedOption) =>
																	setSelectedPrefijo(
																		selectedOption ? selectedOption.value : ""
																	)
																}
																options={prefijoOptions}
																classNamePrefix="select2-selection"
																placeholder="Prefijo..."
																isClearable
																isDisabled={
																	isSpecialMode || prefijoOptions.length === 0
																}
																styles={{
																	...customStyles,
																	control: (provided, state) => ({
																		...provided,
																		minHeight: "38px",
																		borderColor: state.isFocused
																			? "#6c757d"
																			: "#ced4da",
																		boxShadow: state.isFocused
																			? "0 0 0 0.2rem rgba(108, 117, 125, 0.25)"
																			: "none",
																		"&:hover": {
																			borderColor: "#6c757d",
																		},
																	}),
																}}
															/>
														</FormGroup>
													</Col>

													<Col lg={1} md={6}>
														<FormGroup className="mb-2">
															<Label
																for="folio"
																className="form-label fw-medium small">
																<i className="mdi mdi-file-outline me-1 text-info"></i>
																Folio
															</Label>
															<Input
																type="text"
																id="folio"
																value={folio}
																onChange={(e) => setFolio(e.target.value)}
																placeholder="Número folio..."
																className="form-control form-control-sm"
																disabled={isSpecialMode}
																style={{
																	borderColor: "#ced4da",
																	transition:
																		"border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
																}}
																onFocus={(e) => {
																	e.target.style.borderColor = "#17a2b8";
																	e.target.style.boxShadow =
																		"0 0 0 0.2rem rgba(23, 162, 184, 0.25)";
																}}
																onBlur={(e) => {
																	e.target.style.borderColor = "#ced4da";
																	e.target.style.boxShadow = "none";
																}}
															/>
														</FormGroup>
													</Col>

													<Col lg={3} md={6}>
														<FormGroup className="mb-2">
															<Label
																for="nitReceptor"
																className="form-label fw-medium small">
																<i className="mdi mdi-account me-1 text-primary"></i>
																NIT Receptor
															</Label>
															<Input
																type="text"
																id="nitReceptor"
																value={nitReceptor}
																onChange={(e) => setNitReceptor(e.target.value)}
																placeholder="NIT receptor..."
																className="form-control form-control-sm"
																disabled={isSpecialMode}
																style={{
																	borderColor: "#ced4da",
																	transition:
																		"border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
																}}
																onFocus={(e) => {
																	e.target.style.borderColor = "#556ee6";
																	e.target.style.boxShadow =
																		"0 0 0 0.2rem rgba(85, 110, 230, 0.25)";
																}}
																onBlur={(e) => {
																	e.target.style.borderColor = "#ced4da";
																	e.target.style.boxShadow = "none";
																}}
															/>
														</FormGroup>
													</Col>

													<Col lg={3} md={12}>
														<div className="d-flex align-items-end h-100">
															<div className="btn-group w-100" role="group">
																<Button
																	type="submit"
																	color="primary"
																	size="sm"
																	className="btn-action">
																	<i className="mdi mdi-magnify me-1"></i>
																	<span className="d-none d-lg-inline">
																		Consultar
																	</span>
																</Button>
																<Button
																	color="outline-secondary"
																	size="sm"
																	onClick={limpiarFiltros}
																	className="btn-action">
																	<i className="mdi mdi-refresh me-1"></i>
																	<span className="d-none d-lg-inline">
																		Limpiar
																	</span>
																</Button>
																<ButtonDropdown
																	isOpen={exportDropdownOpen}
																	toggle={() => setExportDropdownOpen(!exportDropdownOpen)}>
																	<DropdownToggle
																		color="success"
																		size="sm"
																		disabled={loadingExport}
																		caret
																		className="btn-action">
																		{loadingExport ? (
																			<>
																				<span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
																				Exportando…
																			</>
																		) : (
																			<>
																				<i className="mdi mdi-download me-1"></i>
																				<span className="d-none d-lg-inline">Exportar</span>
																			</>
																		)}
																	</DropdownToggle>
																	<DropdownMenu end>
																		<DropdownItem onClick={() => exportarComprobantes("xlsx")}>
																			<i className="mdi mdi-file-excel me-2 text-success"></i>
																			Excel (.xlsx)
																		</DropdownItem>
																		<DropdownItem onClick={() => exportarComprobantes("csv")}>
																			<i className="mdi mdi-file-delimited me-2 text-primary"></i>
																			CSV (.csv)
																		</DropdownItem>
																	</DropdownMenu>
																</ButtonDropdown>
															</div>
														</div>
													</Col>
												</Row>
											</CardBody>
										</Card>
									</Form>
								</CardBody>
							</Card>
							<hr />

							{loading ? (
								<div className="text-center">
									<Spinner color="primary" />
									<p>Cargando facturas...</p>
								</div>
							) : (
								<div>
									<div>Total registros: {totalDocs}</div>

									{facturas.length === 0 ? (
										<div className="text-center mt-4">
											<p className="text-muted">
												{totalDocs === 0
													? "No se encontraron facturas con los filtros aplicados."
													: "No hay datos para mostrar."}
											</p>
										</div>
									) : (
										<>
											{/* Resumen de totales */}
											{facturas.length > 0 && (
												<div className="mb-4 p-3 bg-light rounded border">
													<div className="d-flex justify-content-between align-items-center mb-3">
														<h6 className="text-secondary mb-0">
															<i className="fas fa-calculator me-2"></i>Resumen
															de Totales
														</h6>
														<small className="text-muted">
															{facturas.length} de {totalDocs} registros
														</small>
													</div>
													<Row>
														<Col md="2" className="text-center">
															<div className="border-end">
																<div className="text-muted small mb-1">
																	Subtotal
																</div>
																<div className="h6 text-success mb-0">
																	{formatCurrency(totalSubtotal)}
																</div>
															</div>
														</Col>
														<Col md="2" className="text-center">
															<div className="border-end">
																<div className="text-muted small mb-1">
																	IVA Total
																</div>
																<div className="h6 text-warning mb-0">
																	{formatCurrency(totalIva)}
																</div>
															</div>
														</Col>
														<Col md="2" className="text-center">
															<div className="border-end">
																<div className="text-muted small mb-1">
																	INC Total
																</div>
																<div className="h6 text-info mb-0">
																	{formatCurrency(totalInc)}
																</div>
															</div>
														</Col>
														<Col md="1" className="text-center">
															<div className="border-end">
																<div className="text-muted small mb-1">
																	Otros
																</div>
																<div className="h6 text-danger mb-0">
																	{formatCurrency(totalOtrosImpuestos)}
																</div>
															</div>
														</Col>
														<Col md="1" className="text-center">
															<div className="border-end">
																<div className="text-muted small mb-1">
																	Propina
																</div>
																<div className="h6 text-success mb-0">
																	{formatCurrency(totalCargoPropina)}
																</div>
															</div>
														</Col>
														<Col md="2" className="text-center">
															<div className="border-end">
																<div className="text-muted small mb-1">
																	Total Impuestos
																</div>
																<div className="h6 text-info mb-0">
																	{formatCurrency(totalImpuestos)}
																</div>
															</div>
														</Col>
														<Col md="2" className="text-center">
															<div>
																<div className="text-muted small mb-1">
																	Total General
																</div>
																<div className="h5 text-dark fw-bold mb-0">
																	{formatCurrency(totalGeneral)}
																</div>
															</div>
														</Col>
													</Row>
												</div>
											)}

											<div className="table-container-with-fixed-header">
												<div className="table-responsive-custom">
													<table className="table table-striped table-bordered table-hover sticky-header-table" style={{ fontSize: "12px" }}>
														<thead className="sticky-header">
															<tr>
																<th className="text-center" style={{ width: "36px", minWidth: "36px", cursor: "pointer" }}
																	onClick={toggleSelectAll}>
																	<input type="checkbox" readOnly
																		checked={facturas.length > 0 && selectedCount === facturas.filter(f => f.cufe).length}
																		style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#556ee6", pointerEvents: "none", padding: 0, margin: 0, border: "1px solid #adb5bd", borderRadius: 3 }}
																		title="Seleccionar todos" />
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "110px", width: "110px" }}>
																	<i className="fas fa-cogs"></i> Acciones
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-file-alt"></i> Tipo Doc
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "300px" }}>
																	<i className="fas fa-fingerprint"></i> CUFE
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-calendar"></i> Fecha
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-calendar-plus"></i> Fecha
																	Gen.
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-calendar-times"></i> Fecha Venc.
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "130px" }}>
																	<i className="fas fa-briefcase"></i> Fecha Negocio
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "100px" }}>
																	<i className="fas fa-tag"></i> Prefijo
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "100px" }}>
																	<i className="fas fa-hashtag"></i> Folio
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-id-card"></i> NIT Emisor
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "150px" }}>
																	<i className="fas fa-building"></i> Emisor
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-id-card"></i> NIT
																	Receptor
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "150px" }}>
																	<i className="fas fa-user"></i> Receptor
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "130px" }}>
																	<i className="fas fa-user-tag"></i> Tipo Receptor
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "150px" }}>
																	<i className="fas fa-address-card"></i> Tipo Doc Receptor
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "150px" }}>
																	<i className="fas fa-store"></i> Nombre Comercial
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-file-contract"></i>{" "}
																	Resolución
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-credit-card"></i>{" "}
																	Forma de Pago
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "130px" }}>
																	<i className="fas fa-check-circle"></i> Datáfono Aprob.
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-calendar"></i> Datáfono F. Pago
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-clock"></i> Datáfono H. Pago
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "130px" }}>
																	<i className="fas fa-credit-card"></i> Datáfono Medio
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "130px" }}>
																	<i className="fas fa-dollar-sign"></i> Datáfono Valor
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "150px" }}>
																	<i className="fas fa-envelope"></i>{" "}
																	Correo Envío
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "150px" }}>
																	<i className="fas fa-paper-plane"></i>{" "}
																	Eventos Correo
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-dollar-sign"></i>{" "}
																	Subtotal
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-percent"></i> IVA
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-plus"></i> INC
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-calculator"></i> Otros
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-receipt"></i> Total Imp.
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-hand-holding-usd"></i>{" "}
																	Cargo/Propina
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-money-bill-wave"></i>{" "}
																	Total
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "150px" }}>
																	<i className="fas fa-store"></i> Sucursal
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "200px" }}>
																	<i className="fas fa-map-marker-alt"></i> Dirección Punto
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "130px" }}>
																	<i className="fas fa-phone"></i> Teléfono Punto
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "200px" }}>
																	<i className="fas fa-at"></i> Correo Punto
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "140px" }}>
																	<i className="fas fa-cash-register"></i> Estación
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "100px" }}>
																	<i className="fas fa-tags"></i> BrandID
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "100px" }}>
																	<i className="fas fa-store-alt"></i> StoreID
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-receipt"></i> Folio POS
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "100px" }}>
																	<i className="fas fa-check-circle"></i>{" "}
																	CheckID
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-shopping-cart"></i> Orden
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "130px" }}>
																	<i className="fas fa-moped"></i> Canal Delivery
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-clipboard-list"></i> Orden Delivery
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "130px" }}>
																	<i className="fas fa-link"></i> Doc. Referencia
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "120px" }}>
																	<i className="fas fa-calendar-day"></i> Fecha Ref.
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "250px" }}>
																	<i className="fas fa-fingerprint"></i> CUFE Referencia
																</th>
																<th
																	className="text-center"
																	style={{ minWidth: "110px" }}>
																	<i className="fas fa-tag"></i> Concepto
																</th>
															</tr>
														</thead>
														<tbody>
															{facturas.map((factura, index) => (
																<tr
																	key={factura.cufe || index}
																	className={`align-middle${selectedCufes[factura.cufe] ? ' table-primary' : ''}`}>
																	<td className="text-center" style={{ cursor: "pointer" }}
																		onClick={() => toggleSelect(factura.cufe)}>
																		<input type="checkbox" readOnly
																			checked={!!selectedCufes[factura.cufe]}
																			style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#556ee6", pointerEvents: "none", padding: 0, margin: 0, border: "1px solid #adb5bd", borderRadius: 3 }} />
																	</td>
																	<td className="text-center" style={{ whiteSpace: "nowrap" }}>
																		<div className="d-inline-flex align-items-center gap-1">
																			<button type="button" className="btn btn-soft-info btn-sm" title="DIAN"
																				style={{ padding: "3px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #d0e7f7", background: "#eaf4fc", color: "#3b82c4" }}
																				onClick={() => handleDianClick(factura.cufe)}>
																				<i className="fas fa-building"></i>
																			</button>
																			<button type="button" className="btn btn-soft-primary btn-sm" title="Ver Detalle"
																				style={{ padding: "3px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #c3cbe4", background: "#eef2ff", color: "#556ee6" }}
																				onClick={() => handleDetalleClick(factura.cufe)}>
																				<i className="fas fa-eye"></i>
																			</button>
																			<button type="button" className="btn btn-soft-danger btn-sm" title="Descargar PDF"
																				style={{ padding: "3px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #f5c6cb", background: "#fdecea", color: "#dc3545" }}
																				onClick={() => handlePDFClick(factura.cufe, factura.nitemisor, factura.tipocomprobante)}
																				disabled={loadingPDFs[factura.cufe]}>
																				{loadingPDFs[factura.cufe] ? <Spinner size="sm" color="danger" style={{ width: 12, height: 12 }} /> : <i className="fas fa-file-pdf"></i>}
																			</button>
																			<button type="button" className="btn btn-soft-success btn-sm" title="Descargar ZIP firmado"
																				style={{ padding: "3px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #b7e4c7", background: "#e6f7ec", color: "#198754" }}
																				onClick={() => handleZipClick(factura.cufe, factura)}
																				disabled={loadingZips[factura.cufe]}>
																				{loadingZips[factura.cufe] ? <Spinner size="sm" color="success" style={{ width: 12, height: 12 }} /> : <i className="fas fa-file-archive"></i>}
																			</button>
																			{factura.nitreceptor !== "222222222222" && (
																				<button type="button" className="btn btn-soft-warning btn-sm" title="Trazabilidad Correo"
																					style={{ padding: "3px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #fde4d0", background: "#fff8e6", color: "#e67e22" }}
																					onClick={() => openCorreoModal(factura)}>
																					<i className="fas fa-envelope"></i>
																				</button>
																			)}
																		</div>
																	</td>
																	{/* Tipo Comprobante */}
																	<td style={{ maxWidth: "150px" }}>
																		<span style={{
																			fontSize: "10px",
																			color: "#495057",
																			lineHeight: 1.3,
																			display: "block",
																		}}>
																			{getDocumentoNombre(
																				factura.tipocomprobante
																			)}
																		</span>
																	</td>
																	{/* CUFE */}
																	<td
																		className="text-break"
																		style={{ maxWidth: "300px" }}>
																		<small className="font-monospace">
																			{factura.cufe || "N/A"}
																		</small>
																	</td>
																	{/* Fecha */}
																	<td className="text-center">
																		<span className="badge bg-primary">
																			{formatFecha(factura.fecha)}
																		</span>
																	</td>
																	{/* Fecha Generación */}
																	<td className="text-center">
																		<span className="badge bg-secondary">
																			{formatFecha(factura.fechageneracion)}
																		</span>
																	</td>
																	{/* Fecha Vencimiento */}
																	<td className="text-center">
																		<span className="badge bg-secondary">
																			{formatFecha(factura.fecha_vencimiento)}
																		</span>
																	</td>
																	{/* Fecha Negocio */}
																	<td className="text-center">
																		<span className="badge bg-secondary">
																			{formatFecha(factura.fecha_negocio)}
																		</span>
																	</td>
																	{/* Prefijo */}
																	<td className="text-center">
																		<span
																			className="badge bg-secondary"
																			id={`prefijo-${index}`}
																			style={{ cursor: "pointer" }}>
																			{factura.prefijo || "N/A"}
																		</span>
																		{factura.prefijo && (
																			<UncontrolledTooltip
																				placement="top"
																				target={`prefijo-${index}`}
																				transition={{ timeout: 250 }}>
																				Prefijo: {factura.prefijo}
																			</UncontrolledTooltip>
																		)}
																	</td>
																	{/* Folio */}
																	<td className="text-center">
																		<strong
																			id={`folio-${index}`}
																			style={{ cursor: "pointer" }}>
																			{factura.folio || "N/A"}
																		</strong>
																		{factura.folio && (
																			<UncontrolledTooltip
																				placement="top"
																				target={`folio-${index}`}
																				transition={{ timeout: 250 }}>
																				Número de folio: {factura.folio}
																			</UncontrolledTooltip>
																		)}
																	</td>
																	{/* NIT Emisor */}
																	<td className="text-center">
																		<code>{factura.nitemisor || "N/A"}</code>
																	</td>
																	{/* Nombre Emisor */}
																	<td
																		className="text-truncate"
																		style={{ maxWidth: "150px" }}>
																		<span title={factura.nombreemisor}>
																			{factura.nombreemisor || "N/A"}
																		</span>
																	</td>
																	{/* NIT Receptor */}
																	<td className="text-center">
																		<code>{factura.nitreceptor || "N/A"}</code>
																	</td>
																	{/* Nombre Receptor */}
																	<td
																		className="text-truncate"
																		style={{ maxWidth: "150px" }}>
																		<span title={factura.nombrereceptor}>
																			{factura.nombrereceptor || "N/A"}
																		</span>
																	</td>
																	{/* Tipo Receptor */}
																	<td className="text-center">
																		<small>{getTipoReceptorNombre(factura.tipo_receptor)}</small>
																	</td>
																	{/* Tipo Doc Receptor */}
																	<td className="text-center">
																		<small>{getTipoDocReceptorNombre(factura.tipo_doc_receptor)}</small>
																	</td>
																	{/* Nombre Comercial Receptor */}
																	<td className="text-truncate" style={{ maxWidth: "150px" }}>
																		<span title={factura.nombre_comercial_receptor}>
																			{factura.nombre_comercial_receptor || "N/A"}
																		</span>
																	</td>
																	{/* Resolución */}
																	<td className="text-center">
																		<small>{factura.resolucion || "N/A"}</small>
																	</td>
																	{/* Forma de Pago */}
																	<td className="text-center">
																		<small>{formaPagoLabel(factura)}</small>
																	</td>
																	{/* Datáfono Aprobación */}
																	<td className="text-center">
																		<small>{factura.datafono_aprobacion || "—"}</small>
																	</td>
																	{/* Datáfono Fecha Pago */}
																	<td className="text-center">
																		<small>{factura.datafono_fecha_pago ? formatFecha(factura.datafono_fecha_pago) : "—"}</small>
																	</td>
																	{/* Datáfono Hora Pago */}
																	<td className="text-center">
																		<small>{factura.datafono_hora_pago || "—"}</small>
																	</td>
																	{/* Datáfono Medio */}
																	<td className="text-center">
																		<small>{factura.datafono_medio || "—"}</small>
																	</td>
																	{/* Datáfono Valor */}
																	<td className="text-end">
																		<span className="fw-bold">{factura.datafono_valor ? formatCurrency(factura.datafono_valor) : "—"}</span>
																	</td>
																	{/* Correo Envío */}
																	<td className="text-center">
																		<small>{factura.correos_enviados || ""}</small>
																	</td>
																	{/* Eventos Correo */}
																	<td className="text-center">
																		<small>{factura.eventos_correo ?? ""}</small>
																	</td>
																	{/* Subtotal */}
																	<td className="text-end">
																		<span className="text-success fw-bold">
																			{formatCurrency(factura.subtotal || 0)}
																		</span>
																	</td>
																	{/* IVA Total */}
																	<td className="text-end">
																		<span
																			className="tax-group-iva fw-bold clickable-amount"
																			id={`iva-total-${index}`}>
																			{formatCurrency(
																				(factura.iva0 || 0) +
																				(factura.iva5 || 0) +
																				(factura.iva16 || 0) +
																				(factura.iva19 || 0)
																			)}
																		</span>
																		<UncontrolledTooltip
																			placement="top"
																			target={`iva-total-${index}`}
																			transition={{ timeout: 250 }}>
																			<div>
																				<div>
																					<strong>Desglose IVA:</strong>
																				</div>
																				<div>
																					• IVA 0%:{" "}
																					{formatCurrency(factura.iva0 || 0)}
																				</div>
																				<div>
																					• IVA 5%:{" "}
																					{formatCurrency(factura.iva5 || 0)}
																				</div>
																				<div>
																					• IVA 16%:{" "}
																					{formatCurrency(factura.iva16 || 0)}
																				</div>
																				<div>
																					• IVA 19%:{" "}
																					{formatCurrency(factura.iva19 || 0)}
																				</div>
																			</div>
																		</UncontrolledTooltip>
																	</td>
																	{/* INC Total */}
																	<td className="text-end">
																		<span
																			className="tax-group-inc fw-bold clickable-amount"
																			id={`inc-total-${index}`}>
																			{formatCurrency(
																				(factura.inc2 || 0) +
																				(factura.inc4 || 0) +
																				(factura.inc8 || 0) +
																				(factura.inc16 || 0)
																			)}
																		</span>
																		<UncontrolledTooltip
																			placement="top"
																			target={`inc-total-${index}`}
																			transition={{ timeout: 250 }}>
																			<div>
																				<div>
																					<strong>Desglose INC:</strong>
																				</div>
																				<div>
																					• INC 2%:{" "}
																					{formatCurrency(factura.inc2 || 0)}
																				</div>
																				<div>
																					• INC 4%:{" "}
																					{formatCurrency(factura.inc4 || 0)}
																				</div>
																				<div>
																					• INC 8%:{" "}
																					{formatCurrency(factura.inc8 || 0)}
																				</div>
																				<div>
																					• INC 16%:{" "}
																					{formatCurrency(factura.inc16 || 0)}
																				</div>
																			</div>
																		</UncontrolledTooltip>
																	</td>
																	{/* Otros Impuestos Total */}
																	<td className="text-end">
																		<span
																			className="tax-group-otros fw-bold clickable-amount"
																			id={`otros-total-${index}`}>
																			{formatCurrency(
																				(factura.inpp || 0) +
																				(factura.ibua || 0) +
																				(factura.icui || 0) +
																				(factura.icl || 0) +
																				(factura.otrosimpuestos || 0)
																			)}
																		</span>
																		<UncontrolledTooltip
																			placement="top"
																			target={`otros-total-${index}`}
																			transition={{ timeout: 250 }}>
																			<div>
																				<div>
																					<strong>Desglose Otros:</strong>
																				</div>
																				<div>
																					• INPP:{" "}
																					{formatCurrency(factura.inpp || 0)}
																				</div>
																				<div>
																					• IBUA:{" "}
																					{formatCurrency(factura.ibua || 0)}
																				</div>
																				<div>
																					• ICUI:{" "}
																					{formatCurrency(factura.icui || 0)}
																				</div>
																				<div>
																					• ICL:{" "}
																					{formatCurrency(factura.icl || 0)}
																				</div>
																				<div>
																					• Otros:{" "}
																					{formatCurrency(
																						factura.otrosimpuestos || 0
																					)}
																				</div>
																			</div>
																		</UncontrolledTooltip>
																	</td>
																	{/* Total Impuestos */}
																	<td className="text-end">
																		<span className="text-info fw-bold">
																			{formatCurrency(
																				factura.impuestototal || 0
																			)}
																		</span>
																	</td>
																	{/* Cargo/Propina */}
																	<td className="text-end">
																		<span
																			className={`money-value fw-bold ${factura.cargo_propina > 0 ? "money-positive cargo-propina-highlight" : "money-zero"}`}>
																			{formatCurrency(
																				factura.cargo_propina || 0
																			)}
																		</span>
																	</td>
																	{/* Total */}
																	<td className="text-end">
																		<span className="text-primary fw-bold fs-6">
																			{formatCurrency(factura.total || 0)}
																		</span>
																	</td>
																	{/* Sucursal */}
																	<td
																		className="text-truncate"
																		style={{ maxWidth: "150px" }}>
																		<span title={factura.sucursal}>
																			{factura.sucursal || "N/A"}
																		</span>
																	</td>
																	{/* Dirección Punto */}
																	<td className="text-truncate" style={{ maxWidth: "200px" }}>
																		<span title={factura.direccion_punto}>
																			{factura.direccion_punto || "N/A"}
																		</span>
																	</td>
																	{/* Teléfono Punto */}
																	<td className="text-center">
																		<small>{factura.telefono_punto || "N/A"}</small>
																	</td>
																	{/* Correo Punto */}
																	<td className="text-truncate" style={{ maxWidth: "200px" }}>
																		<span title={factura.correo_punto}>
																			{factura.correo_punto || "N/A"}
																		</span>
																	</td>
																	{/* Estación */}
																	<td className="text-center">
																		<span className="badge bg-light text-dark">
																			{factura.estacion || "N/A"}
																		</span>
																	</td>
																	{/* BrandID */}
																	<td className="text-center">
																		<span className="badge bg-light text-dark">
																			{factura.brandid || "N/A"}
																		</span>
																	</td>
																	{/* StoreID */}
																	<td className="text-center">
																		<span className="badge bg-light text-dark">
																			{factura.storeid || "N/A"}
																		</span>
																	</td>
																	{/* Folio POS */}
																	<td className="text-center">
																		<span className="badge bg-info">
																			{factura.foliopost || "N/A"}
																		</span>
																	</td>
																	{/* CheckID */}
																	<td className="text-center">
																		<span className="badge bg-success">
																			{factura.checkid || "N/A"}
																		</span>
																	</td>
																	{/* Identificador Orden */}
																	<td className="text-center">
																		<span className="badge bg-warning text-dark">
																			{factura.identificadorordr || "N/A"}
																		</span>
																	</td>
																	{/* Canal Delivery */}
																	<td className="text-center">
																		<small>{factura.dv_canal || "—"}</small>
																	</td>
																	{/* Orden Delivery */}
																	<td className="text-center">
																		<small>{factura.dv_orden || "—"}</small>
																	</td>
																	{/* Documento Referencia */}
																	<td className="text-center">
																		<small>{factura.documento_referencia || "—"}</small>
																	</td>
																	{/* Fecha Referencia */}
																	<td className="text-center">
																		<small>{factura.fecha_referencia ? formatFecha(factura.fecha_referencia) : "—"}</small>
																	</td>
																	{/* CUFE Referencia */}
																	<td className="text-break" style={{ maxWidth: "250px" }}>
																		{factura.cufe_referencia
																			? <small className="font-monospace" title={factura.cufe_referencia}>{factura.cufe_referencia}</small>
																			: <small className="text-muted">—</small>}
																	</td>
																	{/* Concepto */}
																	<td className="text-center">
																		<small>{factura.concepto || "—"}</small>
																	</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</div>

											<div className="d-flex justify-content-between align-items-center mt-3">
												<div>Total registros: {totalDocs}</div>
												<ReactPaginate
													previousLabel={"← Anterior"}
													nextLabel={"Siguiente →"}
													pageCount={totalPages}
													forcePage={currentPage}
													onPageChange={handlePageClick}
													containerClassName={"pagination"}
													activeClassName={"active"}
													pageClassName={"page-item"}
													pageLinkClassName={"page-link"}
													previousClassName={"page-item"}
													previousLinkClassName={"page-link"}
													nextClassName={"page-item"}
													nextLinkClassName={"page-link"}
													breakClassName={"page-item"}
													breakLinkClassName={"page-link"}
												/>
											</div>
										</>
									)}
								</div>
							)}
							{/* Floating bulk action bar */}
							{selectedCount > 0 && (
								<div style={{
									position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1050,
									background: "linear-gradient(135deg, #343a40 0%, #1a1e23 100%)",
									color: "#fff", padding: "12px 24px",
									display: "flex", alignItems: "center", justifyContent: "center", gap: 16,
									boxShadow: "0 -4px 20px rgba(0,0,0,0.25)",
								}}>
									<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
										<i className="fas fa-check-square" style={{ fontSize: 18, color: "#556ee6" }}></i>
										<span style={{ fontWeight: 600, fontSize: 14 }}>
											{selectedCount} comprobante{selectedCount > 1 ? "s" : ""} seleccionado{selectedCount > 1 ? "s" : ""}
										</span>
									</div>
									{bulkDownloading ? (
										<div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 320 }}>
											<Spinner size="sm" color="light" />
											<div style={{ flex: 1 }}>
												<div style={{ fontSize: 12, marginBottom: 4 }}>
													{bulkType === "zip" ? "Descargando ZIPs firmados" : "Descargando PDFs"} {bulkProgress.current} de {bulkProgress.total}...
													{bulkProgress.errors > 0 && <span style={{ color: "#f1b44c" }}> ({bulkProgress.errors} error{bulkProgress.errors > 1 ? "es" : ""})</span>}
												</div>
												<div style={{ background: "rgba(255,255,255,0.2)", borderRadius: 4, height: 6 }}>
													<div style={{
														background: bulkType === "zip" ? "#74b9ff" : "#34c38f",
														borderRadius: 4, height: 6,
														width: `${bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0}%`,
														transition: "width 0.3s",
													}} />
												</div>
											</div>
										</div>
									) : (
										<>
											<Button color="success" size="sm" onClick={handleBulkPDFDownload}
												style={{ borderRadius: 8, fontWeight: 600, padding: "6px 16px" }}>
												<i className="fas fa-file-pdf me-2"></i>
												PDFs ({selectedCount})
											</Button>
											<Button color="info" size="sm" onClick={handleBulkZipDownload}
												style={{ borderRadius: 8, fontWeight: 600, padding: "6px 16px", color: "#fff" }}
												title="Descargar los ZIPs firmados de cada factura empaquetados en un solo archivo">
												<i className="fas fa-file-archive me-2"></i>
												ZIPs firmados ({selectedCount})
											</Button>
											<Button color="link" size="sm" onClick={clearSelection}
												style={{ color: "#adb5bd", textDecoration: "none", fontSize: 13 }}>
												<i className="fas fa-times me-1"></i> Limpiar
											</Button>
										</>
									)}
								</div>
							)}
							<Modal
								isOpen={correoModalOpen}
								toggle={closeCorreoModal}
								size="lg"
								className="modal-correo-gestion"
								style={{ zIndex: 1070 }}
								backdrop="static"
								modalClassName="modal-correo-wrapper">
								<ModalHeader toggle={closeCorreoModal}>
									<div className="d-flex align-items-center">
										<i className="fas fa-envelope-open-text text-warning me-2"></i>
										<span>Gestión de correo</span>
									</div>
								</ModalHeader>
								<ModalBody style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "auto" }}>
									{correoTarget && (
										<div className="mb-3 small text-muted">
											<div>
												<strong>Documento:</strong> {correoTarget.prefijo || ""}
												{correoTarget.folio || ""}
											</div>
											<div>
												<strong>Emisor:</strong> {correoTarget.nitemisor || "N/A"}
											</div>
											<div>
												<strong>CUFE:</strong> {correoTarget.cufe || "N/A"}
											</div>
										</div>
									)}

									{/* Resumen enriquecido del nuevo endpoint /a7/inbox/{cufe}/full */}
									{correoInboxFull && (() => {
										const doc = correoInboxFull.documento || {};
										const est = correoInboxFull.estado_actual || {};
										const res = correoInboxFull.resumen || {};
										const dest = correoInboxFull.destinatarios || [];
										const estadoCfg = (() => {
											const s = String(est.estado || "").toLowerCase();
											if (s.includes("sent")) return { bg: "#16a34a", label: est.estado, icon: "fa-check-circle" };
											if (s.includes("fail") || s.includes("bounce")) return { bg: "#dc2626", label: est.estado, icon: "fa-times-circle" };
											if (s.includes("processing")) return { bg: "#3b82f6", label: est.estado, icon: "fa-sync" };
											return { bg: "#64748b", label: est.estado || "—", icon: "fa-info-circle" };
										})();
										return (
											<div className="mb-3" style={{
												background: "#f8fafc", border: "1px solid #e2e8f0",
												borderRadius: 10, padding: 14,
											}}>
												{/* Header documento + estado */}
												<div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
													<div style={{ minWidth: 220, flex: 1 }}>
														<div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
															<i className="fas fa-file-invoice me-1" />
															Documento
														</div>
														<div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
															{doc.tipo_documento || "—"} · {doc.prefijo || ""}{doc.folio || ""}
														</div>
														<div style={{ fontSize: 12, color: "#475569" }}>
															<strong>{doc.nombre_emisor || doc.nitemisor || "—"}</strong>
															{doc.sucursal && <> · {doc.sucursal}</>}
														</div>
														<div style={{ fontSize: 12, color: "#475569" }}>
															Receptor: <strong>{doc.nombre_receptor || "—"}</strong>
															{doc.nit_receptor && <> · NIT {doc.nit_receptor}</>}
														</div>
														<div style={{ fontSize: 11, color: "#94a3b8" }}>
															Fecha doc: {doc.fecha_documento ? doc.fecha_documento.substring(0, 10) : "—"}
															{doc.total_factura != null && <> · Total: ${Number(doc.total_factura).toLocaleString("es-CO")}</>}
															{doc.tipopdf && <> · PDF {doc.tipopdf}</>}
														</div>
													</div>
													<div style={{ textAlign: "right", minWidth: 200 }}>
														<span style={{
															padding: "4px 12px", borderRadius: 999,
															background: estadoCfg.bg, color: "#fff",
															fontSize: 12, fontWeight: 700,
															display: "inline-flex", alignItems: "center", gap: 5,
														}}>
															<i className={`fas ${estadoCfg.icon}`} />
															{estadoCfg.label}
														</span>
														{est.ultima_etapa && (
															<div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>
																Etapa: <strong>{est.ultima_etapa}</strong>
															</div>
														)}
														{est.total_intentos != null && (
															<div style={{ fontSize: 11, color: "#475569" }}>
																{est.total_intentos} intento{est.total_intentos !== 1 ? "s" : ""}
															</div>
														)}
														{est.ultimo_intento_at && (
															<div style={{ fontSize: 10, color: "#94a3b8" }}>
																Último: {est.ultimo_intento_at}
															</div>
														)}
														{est.ultimo_error && (
															<div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>
																<i className="fas fa-exclamation-triangle me-1" />
																{est.ultimo_error}
															</div>
														)}
													</div>
												</div>

												{/* KPIs del resumen */}
												<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
													{[
														{ label: "Destinatarios", value: res.total_destinatarios, color: "#3b82f6", icon: "fa-users" },
														{ label: "Enviados a SES", value: res.destinatarios_enviados_a_ses, color: "#0ea5e9", icon: "fa-paper-plane" },
														{ label: "Fallos pre-SES", value: res.destinatarios_fallaron_antes_de_ses, color: "#dc2626", icon: "fa-times" },
														{ label: "Aperturas", value: res.destinatarios_que_abrieron, color: "#10b981", icon: "fa-envelope-open" },
														{ label: "Clics", value: res.destinatarios_que_hicieron_click, color: "#8b5cf6", icon: "fa-mouse-pointer" },
														{ label: "Bounces SES", value: res.destinatarios_con_bounce_real_ses, color: "#f97316", icon: "fa-exclamation-triangle" },
													].map((kpi) => (
														<div key={kpi.label} style={{
															background: "#fff", border: "1px solid #e2e8f0",
															borderRadius: 8, padding: "8px 10px", textAlign: "center",
														}}>
															<div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>
																<i className={`fas ${kpi.icon} me-1`} style={{ color: kpi.color }} />
																{kpi.label}
															</div>
															<div style={{ fontSize: 18, fontWeight: 700, color: kpi.color, marginTop: 2 }}>
																{kpi.value ?? 0}
															</div>
														</div>
													))}
												</div>

												{/* Destinatarios — lista compacta */}
												{dest.length > 0 && (
													<div style={{ marginTop: 12 }}>
														<div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4, marginBottom: 6 }}>
															<i className="fas fa-users me-1" />
															Destinatarios ({dest.length})
														</div>
														<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
															{dest.map((d, i) => {
																const ok = String(d.estado_envio_actual || "").toLowerCase() === "sent";
																const opens = d.resumen_eventos?.Open || 0;
																const clicks = d.resumen_eventos?.Click || 0;
																const bounces = (d.resumen_eventos?.Bounce || 0) + (d.resumen_eventos?.HardBounce || 0) + (d.resumen_eventos?.SoftBounce || 0);
																return (
																	<div key={i} style={{
																		background: "#fff", border: "1px solid #e2e8f0",
																		borderRadius: 8, padding: "8px 12px",
																		display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
																	}}>
																	<div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 200 }}>
																		<i className={`fas ${ok ? "fa-check-circle" : "fa-times-circle"}`}
																			style={{ color: ok ? "#16a34a" : "#dc2626" }} />
																		<div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
																			<div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{d.email}</div>
																			<div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "monospace" }}>
																				{d.message_id ? d.message_id.substring(0, 40) + (d.message_id.length > 40 ? "…" : "") : "—"}
																			</div>
																		</div>
																	</div>
																	<div style={{ display: "flex", gap: 6, fontSize: 11, fontWeight: 600 }}>
																		<span title="Intentos" style={{
																			padding: "2px 8px", borderRadius: 6,
																			background: "#e0f2fe", color: "#075985",
																		}}>
																			<i className="fas fa-redo me-1" />{d.intentos || 0}
																		</span>
																		{opens > 0 && (
																			<span title="Aperturas" style={{
																				padding: "2px 8px", borderRadius: 6,
																				background: "#dcfce7", color: "#166534",
																			}}>
																				<i className="fas fa-envelope-open me-1" />{opens}
																			</span>
																		)}
																		{clicks > 0 && (
																			<span title="Clics" style={{
																				padding: "2px 8px", borderRadius: 6,
																				background: "#ede9fe", color: "#5b21b6",
																			}}>
																				<i className="fas fa-mouse-pointer me-1" />{clicks}
																			</span>
																		)}
																		{bounces > 0 && (
																			<span title="Bounces" style={{
																				padding: "2px 8px", borderRadius: 6,
																				background: "#fee2e2", color: "#991b1b",
																			}}>
																				<i className="fas fa-exclamation-triangle me-1" />{bounces}
																			</span>
																		)}
																	</div>
																</div>
																);
															})}
														</div>
													</div>
												)}
											</div>
										);
									})()}

									<Nav tabs>
										<NavItem>
											<NavLink
												className={correoActiveTab === "timeline" ? "active" : ""}
												style={{ cursor: "pointer" }}
												onClick={() => setCorreoActiveTab("timeline")}>
												<i className="fas fa-stream me-2"></i>
												Línea de tiempo
											</NavLink>
										</NavItem>
										<NavItem>
											<NavLink
												className={correoActiveTab === "trace" ? "active" : ""}
												style={{ cursor: "pointer" }}
												onClick={() => setCorreoActiveTab("trace")}>
												<i className="fas fa-route me-2"></i>
												Detalle de eventos
											</NavLink>
										</NavItem>
										<NavItem>
											<NavLink
												className={correoActiveTab === "resend" ? "active" : ""}
												style={{ cursor: "pointer" }}
												onClick={() => setCorreoActiveTab("resend")}>
												<i className="fas fa-paper-plane me-2"></i>
												Reenvío de correo
											</NavLink>
										</NavItem>
									</Nav>
									<TabContent activeTab={correoActiveTab} className="pt-3">
										<TabPane tabId="timeline">
											{correoTraceLoading ? (
												<div className="d-flex align-items-center gap-2">
													<Spinner size="sm" color="primary" />
													<span className="text-muted">Cargando línea de tiempo...</span>
												</div>
											) : correoTraceNotFound ? (
												<div className="text-center py-5 px-3" style={{ color: "#475569" }}>
													<i className="mdi mdi-email-off-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
													<h6 className="mt-3 mb-1" style={{ color: "#0f172a", fontWeight: 600 }}>
														Este comprobante no cuenta con trazabilidad de correo
													</h6>
													<p className="mb-0" style={{ fontSize: 13, maxWidth: 460, margin: "0 auto" }}>
														No hay registros de envío para esta factura. Puede que el correo todavía no se haya enviado o que se haya procesado antes de habilitar el nuevo sistema de seguimiento.
													</p>
												</div>
											) : correoTraceError ? (
												<div className="alert alert-danger mb-0" role="alert">
													{correoTraceError}
												</div>
											) : (!correoInboxFull || !(correoInboxFull.destinatarios || []).some(d => (d.timeline || []).length > 0)) ? (
												<div className="alert alert-warning mb-0" role="alert">
													No hay eventos en la línea de tiempo para este documento.
												</div>
											) : (
												<div>
													{(correoInboxFull.destinatarios || []).map((dest, idxD) => {
														const tl = dest.timeline || [];
														const nivelStyle = (nivel) => {
															switch (String(nivel || "").toLowerCase()) {
																case "success": return { color: "#16a34a", bg: "#dcfce7", border: "#16a34a" };
																case "error":   return { color: "#dc2626", bg: "#fee2e2", border: "#dc2626" };
																case "warning": return { color: "#d97706", bg: "#fef3c7", border: "#d97706" };
																case "info":    return { color: "#0284c7", bg: "#e0f2fe", border: "#0284c7" };
																default:        return { color: "#64748b", bg: "#f1f5f9", border: "#94a3b8" };
															}
														};
														const actorIcon = (actor) => {
															switch (String(actor || "").toLowerCase()) {
																case "listener":     return "fa-server";
																case "ses":          return "fa-cloud";
																case "destinatario": return "fa-user";
																case "wrapper":      return "fa-cogs";
																default:             return "fa-info-circle";
															}
														};
														const destOk = String(dest.estado_envio_actual || "").toLowerCase() === "sent";
														return (
															<div key={idxD} style={{
																background: "#fff", border: "1px solid #e2e8f0",
																borderLeft: `4px solid ${destOk ? "#16a34a" : "#dc2626"}`,
																borderRadius: 10, padding: "14px 18px", marginBottom: 14,
															}}>
																{/* Header del destinatario */}
																<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 pb-2"
																	style={{ borderBottom: "1px solid #f1f5f9" }}>
																	<div>
																		<div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>
																			<i className="fas fa-envelope me-1" />
																			Destinatario #{idxD + 1}
																		</div>
																		<div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{dest.email}</div>
																	</div>
																	<span style={{
																		padding: "3px 12px", borderRadius: 999,
																		background: destOk ? "#16a34a" : "#dc2626", color: "#fff",
																		fontSize: 11, fontWeight: 700,
																		display: "inline-flex", alignItems: "center", gap: 5,
																	}}>
																		<i className={`fas ${destOk ? "fa-check" : "fa-times"}`} />
																		{(dest.estado_envio_actual || "—").toUpperCase()}
																	</span>
																</div>

																{/* Timeline */}
																{tl.length === 0 ? (
																	<div className="text-muted small text-center py-2">Sin eventos en la línea de tiempo.</div>
																) : (
																	<div style={{ position: "relative", paddingLeft: 36 }}>
																		{/* Línea vertical */}
																		<div style={{
																			position: "absolute", left: 14, top: 8, bottom: 8,
																			width: 2, background: "linear-gradient(to bottom, #e2e8f0, #cbd5e1, #e2e8f0)",
																		}} />
																		{tl.map((ev, idxE) => {
																			const st = nivelStyle(ev.nivel);
																			const atParts = String(ev.at || "").split(" ");
																			const atFecha = atParts[0] || "";
																			const atHora = atParts[1] ? atParts[1].substring(0, 8) : "";
																			return (
																				<div key={idxE} style={{ position: "relative", marginBottom: 14 }}>
																					{/* Dot con icono emoji del back */}
																					<div style={{
																						position: "absolute", left: -36, top: 0,
																						width: 30, height: 30, borderRadius: "50%",
																						background: st.bg, border: `2px solid ${st.border}`,
																						display: "flex", alignItems: "center", justifyContent: "center",
																						fontSize: 14, lineHeight: 1, zIndex: 1,
																					}} title={ev.tipo}>
																						{ev.icon || "•"}
																					</div>
																					{/* Card con info del evento */}
																					<div style={{
																						background: "#fff", border: `1px solid ${st.border}40`,
																						borderLeft: `3px solid ${st.border}`,
																						borderRadius: 8, padding: "8px 12px",
																					}}>
																						<div className="d-flex justify-content-between align-items-start flex-wrap gap-1">
																							<div style={{ flex: 1, minWidth: 200 }}>
																								<div style={{ fontSize: 13, fontWeight: 700, color: st.color }}>
																									{ev.titulo || ev.tipo || "Evento"}
																								</div>
																								{ev.subtitulo && (
																									<div style={{ fontSize: 11, color: "#475569", marginTop: 3, wordBreak: "break-word" }}>
																										{ev.subtitulo}
																									</div>
																								)}
																							</div>
																							<div style={{ textAlign: "right", minWidth: 90 }}>
																								<div style={{ fontSize: 11, color: "#0f172a", fontWeight: 600, fontFamily: "monospace" }}>
																									{atHora || ev.at || "—"}
																								</div>
																								{atFecha && (
																									<div style={{ fontSize: 9, color: "#94a3b8" }}>{atFecha}</div>
																								)}
																							</div>
																						</div>
																						{ev.actor && (
																							<div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
																								<span style={{
																									padding: "1px 8px", borderRadius: 999,
																									background: "#f1f5f9", color: "#475569",
																									fontSize: 10, fontWeight: 600,
																								}}>
																									<i className={`fas ${actorIcon(ev.actor)} me-1`} />
																									{ev.actor}
																								</span>
																								{ev.tipo && (
																									<span style={{
																										padding: "1px 8px", borderRadius: 999,
																										background: st.bg, color: st.color,
																										fontSize: 10, fontWeight: 700, fontFamily: "monospace",
																									}}>
																										{ev.tipo}
																									</span>
																								)}
																							</div>
																						)}
																					</div>
																				</div>
																			);
																		})}
																	</div>
																)}
															</div>
														);
													})}
												</div>
											)}
										</TabPane>
										<TabPane tabId="trace">
											{correoTraceLoading ? (
												<div className="d-flex align-items-center gap-2">
													<Spinner size="sm" color="primary" />
													<span className="text-muted">Cargando trazabilidad...</span>
												</div>
											) : correoTraceNotFound ? (
												<div className="text-center py-5 px-3" style={{ color: "#475569" }}>
													<i className="mdi mdi-email-off-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
													<h6 className="mt-3 mb-1" style={{ color: "#0f172a", fontWeight: 600 }}>
														Este comprobante no cuenta con trazabilidad de correo
													</h6>
													<p className="mb-0" style={{ fontSize: 13, maxWidth: 460, margin: "0 auto" }}>
														No hay registros de envío para esta factura. Puede que el correo todavía no se haya enviado o que se haya procesado antes de habilitar el nuevo sistema de seguimiento.
													</p>
												</div>
											) : correoTraceError ? (
												<div className="alert alert-danger mb-0" role="alert">
													{correoTraceError}
												</div>
											) : correoEventos.length === 0 ? (
												<div className="alert alert-warning mb-0" role="alert">
													No se encontraron eventos de correo para este documento.
												</div>
											) : (
												<div className="correo-eventos-container">
													{correoEventos.map((event, idx) => {
														const eventStyle = getEventColor(event.record_type);
														return (
															<div
																key={`${event.message_id}-${idx}`}
																className="card mb-3 shadow-sm correo-evento-card"
																style={{ borderLeft: `4px solid ${eventStyle.bg}` }}
															>
																<div className="card-header py-2 d-flex justify-content-between align-items-center" style={{ backgroundColor: `${eventStyle.bg}15` }}>
																	<div className="d-flex align-items-center gap-2">
																		<span
																			className="badge"
																			style={{ backgroundColor: eventStyle.bg }}
																		>
																			<i className={`${eventStyle.icon} me-1`}></i>
																			{eventStyle.label}
																		</span>
																		<small className="text-muted">
																			<i className="fas fa-calendar-alt me-1"></i>
																			{event.event_date || "-"} {event.event_time ? event.event_time.split(' ')[1] : ""}
																		</small>
																	</div>
																	<small className="text-muted font-monospace" style={{ fontSize: '0.7rem' }}>
																		ID: {event.message_id ? event.message_id.substring(0, 8) + '...' : '-'}
																	</small>
																</div>
																<div className="card-body py-2">
																	<div className="row g-2 small">
																		{/* Columna 1: Información del destinatario */}
																		<div className="col-md-6">
																			<div className="mb-2">
																				<strong><i className="fas fa-envelope me-1 text-primary"></i>Destinatario:</strong>
																				<div className="text-break">{event.recipient || "-"}</div>
																			</div>
																			<div className="mb-2">
																				<strong><i className="fas fa-user me-1 text-info"></i>Cliente:</strong>
																				<div>{event.cliente || "-"}</div>
																			</div>
																			<div className="mb-2">
																				<strong><i className="fas fa-tag me-1 text-warning"></i>Etiqueta:</strong>
																				<div>{event.tag || "-"}</div>
																			</div>
																		</div>
																		{/* Columna 2: Información del documento */}
																		<div className="col-md-6">
																			<div className="mb-2">
																				<strong><i className="fas fa-file-invoice me-1 text-success"></i>Documento:</strong>
																				<div>{event.documento || "-"}</div>
																			</div>
																			<div className="mb-2">
																				<strong><i className="fas fa-hashtag me-1 text-secondary"></i>ID Documento:</strong>
																				<div className="font-monospace">{event.iddocumento || "-"}</div>
																			</div>
																			<div className="mb-2">
																				<strong><i className="fas fa-building me-1 text-dark"></i>Emisor:</strong>
																				<div>{event.emisor || "-"}</div>
																			</div>
																		</div>
																	</div>
																	{/* Información adicional según el tipo de evento */}
																	{(event.platform || event.user_agent || event.geo_country || event.geo_city) && (
																		<div className="row g-2 small mt-1 pt-2 border-top">
																			<div className="col-12">
																				<strong><i className="fas fa-info-circle me-1 text-info"></i>Información de apertura/clic:</strong>
																			</div>
																			{event.platform && (
																				<div className="col-md-3">
																					<i className="fas fa-desktop me-1"></i>
																					<span className="text-muted">Plataforma:</span> {event.platform}
																				</div>
																			)}
																			{(event.geo_country || event.geo_city) && (
																				<div className="col-md-3">
																					<i className="fas fa-map-marker-alt me-1"></i>
																					<span className="text-muted">Ubicación:</span> {event.geo_city ? `${event.geo_city}, ` : ''}{event.geo_country || '-'}
																				</div>
																			)}
																			{event.user_agent && (
																				<div className="col-md-6">
																					<i className="fas fa-globe me-1"></i>
																					<span className="text-muted">User Agent:</span>
																					<div className="text-truncate small" style={{ maxWidth: '100%' }} title={event.user_agent}>
																						{event.user_agent}
																					</div>
																				</div>
																			)}
																		</div>
																	)}
																	{/* Enlace original (si existe) */}
																	{event.original_link && (
																		<div className="row g-2 small mt-1 pt-2 border-top">
																			<div className="col-12">
																				<strong><i className="fas fa-link me-1 text-primary"></i>Enlace:</strong>
																				<a href={event.original_link} target="_blank" rel="noopener noreferrer" className="ms-2 text-break">
																					{event.original_link}
																				</a>
																			</div>
																		</div>
																	)}
																	{/* Detalles del servidor */}
																	{event.details && (
																		<div className="row g-2 small mt-1 pt-2 border-top">
																			<div className="col-12">
																				<strong><i className="fas fa-server me-1 text-secondary"></i>Detalles del servidor:</strong>
																				<div className="text-muted small mt-1 p-2 bg-light rounded" style={{ wordBreak: 'break-all' }}>
																					{event.details}
																				</div>
																			</div>
																		</div>
																	)}
																	{/* Información técnica adicional */}
																	<div className="row g-2 small mt-1 pt-2 border-top text-muted">
																		<div className="col-md-4">
																			<i className="fas fa-stream me-1"></i>
																			Stream: {event.message_stream || '-'}
																		</div>
																		<div className="col-md-4">
																			<i className="fas fa-server me-1"></i>
																			Server ID: {event.server_id || '-'}
																		</div>
																		<div className="col-md-4">
																			<i className="fas fa-cloud me-1"></i>
																			Plataforma: {event.plataforma || '-'}
																		</div>
																	</div>
																</div>
															</div>
														);
													})}
												</div>
											)}

											{correoTraceMeta && (
												<div className="mt-3 p-3 bg-light rounded border">
													<div className="d-flex justify-content-between align-items-center">
														<div className="small text-muted">
															<div>
																<strong>Total eventos:</strong> {correoTraceMeta.totalEvents}
															</div>
															<div>
																<strong>Message IDs:</strong> {correoTraceMeta.messageIds}
															</div>
															<div>
																<strong>Última consulta:</strong> {correoTraceMeta.timestamp}
															</div>
														</div>
														<Button
															color="danger"
															size="sm"
															className="d-flex align-items-center gap-2"
															onClick={generarCertificadoCorreo}
															disabled={correoEventos.length === 0}
														>
															<i className="fas fa-file-pdf"></i>
															Certificación
														</Button>
													</div>
												</div>
											)}
										</TabPane>
										<TabPane tabId="resend">
											<div className="mb-3">
												<Label for="correoMail">Correo destino</Label>
												<Input
													type="email"
													id="correoMail"
													placeholder="correo@ejemplo.com"
													value={correoMail}
													onChange={(e) => setCorreoMail(e.target.value)}
												/>
											</div>
											<Button
												color="warning"
												disabled={correoResendLoading}
												onClick={handleResendEmail}>
												{correoResendLoading ? (
													<>
														<Spinner size="sm" className="me-2" />
														Enviando...
													</>
												) : (
													<>
														<i className="fas fa-paper-plane me-2"></i>
														Reenviar correo
													</>
												)}
											</Button>
										</TabPane>
									</TabContent>
								</ModalBody>
								<ModalFooter>
									<Button color="secondary" onClick={closeCorreoModal}>
										Cerrar
									</Button>
								</ModalFooter>
							</Modal>
							<Modal
								isOpen={modal}
								toggle={toggleModal}
								size="lg"
								style={{ maxWidth: "80%" }}>
								<ModalHeader toggle={toggleModal}>DIAN Document</ModalHeader>
								<ModalBody>
									<iframe
										src={modalContent}
										style={{ width: "100%", height: "600px" }}
										title="DIAN Document"></iframe>
								</ModalBody>
								<ModalFooter>
									<Button color="secondary" onClick={toggleModal}>
										Cerrar
									</Button>
								</ModalFooter>
							</Modal>
							<DetalleFacturaDrawer
								abierto={detalleDrawerAbierto}
								cufe={detalleCufe}
								onCerrar={() => setDetalleDrawerAbierto(false)}
							/>
							<Modal
								isOpen={detalleModal}
								toggle={toggleDetalleModal}
								size="xl"
								style={{ zIndex: 1060 }}
								backdrop="static">
								<ModalHeader toggle={toggleDetalleModal} style={{ zIndex: 1061 }}>
									<div className="d-flex align-items-center">
										<i className="fas fa-list-alt text-primary me-2" style={{ fontSize: '1.2rem' }}></i>
										<span>Detalle de Conceptos</span>
									</div>
								</ModalHeader>
								<ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', zIndex: 1061 }}>
									{detalleContent && detalleContent.success ? (
										<div>
											{/* Header con resumen */}
											<div className="alert alert-info mb-4">
												<div className="row">
													<div className="col-md-6">
														<h6 className="mb-1">
															<i className="fas fa-shopping-cart me-2"></i>
															Total de Conceptos
														</h6>
														<h4 className="mb-0 text-primary">{detalleContent.count}</h4>
													</div>
													<div className="col-md-6">
														<h6 className="mb-1">
															<i className="fas fa-calculator me-2"></i>
															Total General
														</h6>
														<h4 className="mb-0 text-success">
															{formatCurrency(
																detalleContent.data.reduce((total, item) => total + (item.baseimpuestos + item.impuestolinea), 0)
															)}
														</h4>
													</div>
												</div>
											</div>

											{/* Tabla de conceptos */}
											<div className="table-responsive">
												<table className="table table-striped table-hover">
													<thead className="table-dark">
														<tr>
															<th style={{ minWidth: '60px' }}>#</th>
															<th style={{ minWidth: '200px' }}>Descripción</th>
															<th style={{ minWidth: '80px' }}>Cant.</th>
															<th style={{ minWidth: '120px' }}>Precio Unit.</th>
															<th style={{ minWidth: '120px' }}>Subtotal</th>
															<th style={{ minWidth: '80px' }}>Tasa %</th>
															<th style={{ minWidth: '120px' }}>Impuesto</th>
															<th style={{ minWidth: '120px' }}>Total</th>
														</tr>
													</thead>
													<tbody>
														{detalleContent.data.map((concepto, index) => (
															<tr key={concepto.idConcepto}>
																<td className="text-center">
																	<span className="badge bg-secondary">
																		{concepto.idConcepto}
																	</span>
																</td>
																<td>
																	<div>
																		<strong>{concepto.descripcion}</strong>
																		{concepto.identificacionproductos && (
																			<div className="text-muted small">
																				ID: {concepto.identificacionproductos}
																			</div>
																		)}
																	</div>
																</td>
																<td className="text-center">
																	<span className="badge bg-info">
																		{concepto.cantidad}
																	</span>
																</td>
																<td className="text-end">
																	<span className="fw-semibold">
																		{formatCurrency(concepto.precioUnitario)}
																	</span>
																</td>
																<td className="text-end">
																	<span className="fw-semibold">
																		{formatCurrency(concepto.baseimpuestos)}
																	</span>
																</td>
																<td className="text-center">
																	<span className="badge bg-warning text-dark">
																		{concepto.tasa}%
																	</span>
																</td>
																<td className="text-end">
																	<span className="fw-semibold text-danger">
																		{formatCurrency(concepto.impuestolinea)}
																	</span>
																</td>
																<td className="text-end">
																	<span className="fw-bold text-success">
																		{formatCurrency(concepto.baseimpuestos + concepto.impuestolinea)}
																	</span>
																</td>
															</tr>
														))}
													</tbody>
													<tfoot className="table-light">
														<tr>
															<th colSpan="4" className="text-end">TOTALES:</th>
															<th className="text-end">
																{formatCurrency(
																	detalleContent.data.reduce((total, item) => total + item.baseimpuestos, 0)
																)}
															</th>
															<th></th>
															<th className="text-end text-danger">
																{formatCurrency(
																	detalleContent.data.reduce((total, item) => total + item.impuestolinea, 0)
																)}
															</th>
															<th className="text-end text-success">
																{formatCurrency(
																	detalleContent.data.reduce((total, item) => total + (item.baseimpuestos + item.impuestolinea), 0)
																)}
															</th>
														</tr>
													</tfoot>
												</table>
											</div>

											{/* Información adicional */}
											<div className="row mt-4">
												<div className="col-md-6">
													<div className="card border-primary">
														<div className="card-header bg-primary text-white">
															<h6 className="mb-0">
																<i className="fas fa-info-circle me-2"></i>
																Información de Impuestos
															</h6>
														</div>
														<div className="card-body">
															{detalleContent.data.map((concepto, index) => (
																<div key={concepto.idConcepto} className="mb-2">
																	<small className="text-muted">
																		{concepto.descripcion}:
																	</small>
																	<div className="d-flex justify-content-between">
																		<span>Tipo: {concepto.tipo}</span>
																		<span>Tasa: {concepto.tasa}%</span>
																	</div>
																</div>
															))}
														</div>
													</div>
												</div>
												<div className="col-md-6">
													<div className="card border-success">
														<div className="card-header bg-success text-white">
															<h6 className="mb-0">
																<i className="fas fa-chart-pie me-2"></i>
																Resumen Financiero
															</h6>
														</div>
														<div className="card-body">
															<div className="d-flex justify-content-between mb-2">
																<span>Subtotal:</span>
																<strong>
																	{formatCurrency(
																		detalleContent.data.reduce((total, item) => total + item.baseimpuestos, 0)
																	)}
																</strong>
															</div>
															<div className="d-flex justify-content-between mb-2">
																<span>Impuestos:</span>
																<strong className="text-danger">
																	{formatCurrency(
																		detalleContent.data.reduce((total, item) => total + item.impuestolinea, 0)
																	)}
																</strong>
															</div>
															<hr />
															<div className="d-flex justify-content-between">
																<span className="fw-bold">Total General:</span>
																<strong className="text-success fs-5">
																	{formatCurrency(
																		detalleContent.data.reduce((total, item) => total + (item.baseimpuestos + item.impuestolinea), 0)
																	)}
																</strong>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									) : (
										<div className="text-center py-4">
											<div className="spinner-border text-primary" role="status">
												<span className="visually-hidden">Cargando...</span>
											</div>
											<p className="mt-2 text-muted">Cargando detalles...</p>
										</div>
									)}
								</ModalBody>
								<ModalFooter style={{ zIndex: 1061 }}>
									<Button color="primary" onClick={toggleDetalleModal}>
										<i className="fas fa-times me-2"></i>
										Cerrar
									</Button>
								</ModalFooter>
							</Modal>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</React.Fragment>
	);
};

const mapStateToProps = (state) => {
	return {
		authData: state.Login.user,
		empresaSeleccionada: state.Login.empresaSeleccionada,
	};
};

export default connect(mapStateToProps, { setBreadcrumbItems })(Facturas);
